<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import TalkviaFunnelsAPI from 'dashboard/api/talkviaFunnels';
import TalkviaCreditEnginesAPI from 'dashboard/api/talkviaCreditEngines';
import WhatsappFlowsAPI from 'dashboard/api/whatsappFlows';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { resolveMaximumFileUploadSize } from 'shared/helpers/FileHelper';
import NodeFileField from './components/NodeFileField.vue';
import HttpRequestEditor from './components/HttpRequestEditor.vue';
import ConditionRulesEditor from './components/ConditionRulesEditor.vue';
import {
  METRIC_PERIODS,
  countersPreferenceKey,
  formatMetricCount,
  formatMetricPercent,
  hasPotentialEstimate,
  nodeMetricsFor,
  readCountersPreference,
  writeCountersPreference,
} from './flowMetrics';
import {
  defaultHttpAction,
  httpContextSamples,
  httpVariablesFromNodes,
  upstreamHttpNodes,
  validateHttpAction,
} from './httpRequestNode';
import {
  activeRules,
  branchLabelsForNode,
  conditionFieldOptions,
  conditionSummary,
  normalizeCondition,
  suggestBranchLabel,
  validateCondition,
} from './conditionRules';
import {
  adjacentFlowNode,
  incomingNodes,
  orderedFlowNodes,
  outgoingNodes,
} from './nodeNavigation';
import {
  canConnectNodes,
  clientToCanvasPoint,
  connectionCurvePath,
  findNodeAtPoint,
  hasMovedBeyond,
  inputAnchor,
  outputAnchor,
} from './connectionDrag';
import {
  PRIORITY_OPTIONS,
  availableNodeShortcuts,
  filterNodeShortcuts,
  groupNodeShortcuts,
  isCorbanActionType,
} from './nodeCatalog';

const NODE_WIDTH = 320;
const NODE_HEIGHT = 176;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 1.45;

const copy = {
  eyebrow: 'Automações visuais',
  title: 'Construtor de fluxos Talkvia',
  subtitle:
    'Desenhe automações, funis e ofertas conectando blocos do Talkvia em um canvas visual.',
  actions: {
    addNode: 'Adicionar nó',
    addTrigger: 'Adicionar entrada',
    addWebhookTrigger: 'Adicionar webhook',
    useWebhookTrigger: 'Usar webhook nesta entrada',
    addNextAction: 'Adicionar próxima ação',
    testFlow: 'Testar fluxo',
    clearTest: 'Limpar teste',
    newProject: 'Novo projeto',
    connect: 'Conectar',
    cancelConnect: 'Cancelar conexão',
    save: 'Salvar',
    duplicate: 'Duplicar',
    remove: 'Remover',
    reset: 'Restaurar exemplo',
    fit: 'Centralizar',
    openLink: 'Abrir atalho',
    close: 'Fechar',
    copyWebhookUrl: 'Copiar URL',
    refreshWebhook: 'Buscar último JSON',
    waitWebhookTest: 'Aguardar webhook e testar',
    editNode: 'Editar nó',
    done: 'Concluir',
    removeNode: 'Excluir nó',
    confirmRemoveNode: 'Excluir definitivamente',
    cancel: 'Cancelar',
    manageWhatsappFlows: 'Gerenciar WhatsApp Flows',
    refreshWhatsappFlows: 'Atualizar lista de Flows',
    collapsePanel: 'Recolher painel',
    expandPanel: 'Abrir painel',
  },
  status: {
    loading: 'Carregando flow...',
    saving: 'Salvando...',
    saved: 'Salvo',
    error: 'Não foi possível salvar',
  },
  metrics: {
    nodes: 'Nós',
    edges: 'Conexões',
    leads: 'Leads',
    conversion: 'Conversão média',
    leadsHelp:
      'Conversas distintas que passaram por algum nó do fluxo no período.',
    conversionHelp:
      'Conversas que chegaram a um nó Fim (ou ao último nó) ÷ conversas que entraram pelas Entradas.',
    showCounters: 'Mostrar contadores',
    period: 'Período das métricas',
    loadError: 'Não foi possível carregar os contadores do fluxo.',
    nodeLeads: 'Leads',
    nodeConversion: 'Conv.',
    nodePotential: 'Pot. (est.)',
    nodeLeadsHelp: 'Conversas distintas que passaram por este nó no período.',
    nodeConversionHelp:
      'Leads deste nó ÷ conversas que entraram pelas Entradas do fluxo.',
    nodePotentialHelp:
      'Estimativa informada manualmente no nó; não é calculada pelo sistema.',
    realMetrics: 'Contadores reais',
    executions: 'Passagens',
    lastExecution: 'Última passagem',
    noExecution: 'Nenhuma passagem no período.',
  },
  form: {
    name: 'Nome do fluxo',
    nodeTitle: 'Título',
    description: 'Descrição',
    source: 'Origem',
    nodeType: 'Tipo do nó',
    linkType: 'Atalho Talkvia',
    leads: 'Leads',
    conversion: 'Conversão',
    investment: 'Investimento (estimativa)',
    potential: 'Ganho potencial (estimativa)',
    color: 'Cor',
    project: 'Projeto',
    inbox: 'Caixa de entrada',
    status: 'Status do funil',
    actionType: 'Então fazer',
    actionMessage: 'Mensagem/template',
    actionPhonePath: 'Número do contato no JSON',
    actionContactPath: 'Nome do contato no JSON',
    actionMessagePath: 'Mensagem do JSON',
    actionDelay: 'Aguardar minutos',
    creditEngine: 'Motor de crédito',
    actionFile: 'Arquivo para o contato',
    actionFileCaption: 'Legenda do arquivo',
    webhookUrl: 'URL do webhook',
    webhookMethod: 'Método',
    triggerType: 'Quando isso acontecer',
    triggerWebhookEndpoint: 'URL de entrada',
    triggerWebhookMethod: 'Método da entrada',
    triggerWebhookSample: 'JSON de teste',
    triggerWebhookLastPayload: 'Último JSON recebido',
    jsonContactPath: 'Campo do nome',
    jsonPhonePath: 'Campo do telefone',
    jsonMessagePath: 'Campo da mensagem/etiqueta',
    triggerLabel: 'Etiqueta do cliente',
    triggerInbox: 'Caixa de entrada do gatilho',
    triggerKeyword: 'Palavra-chave',
  },
  nodeTypes: {
    trigger: 'Entrada',
    action: 'Ação',
    decision: 'Condição',
    wait: 'Espera',
    offer: 'Oferta',
    end: 'Fim',
  },
  actionTypes: {
    none: 'Apenas etapa visual',
    send_message: 'Enviar mensagem',
    send_file: 'Enviar arquivo',
    add_label: 'Adicionar etiqueta',
    move_inbox: 'Mover para caixa',
    request_best_offer: 'Buscar melhor oferta',
    create_contract: 'Gerar contrato/formalização',
    assign_human: 'Chamar atendimento humano',
    call_webhook: 'Chamar webhook',
    run_credit_engine: 'Executar motor de crédito',
    send_whatsapp_flow: 'Enviar WhatsApp Flow',
    add_private_note: 'Nota privada',
    assign_team: 'Atribuir equipe',
    set_priority: 'Definir prioridade',
    remove_label: 'Remover etiqueta',
    http_request: 'Requisição HTTP',
  },
  triggerTypes: {
    conversation_created: 'Cliente iniciou conversa',
    label_added: 'Cliente recebeu etiqueta',
    message_contains: 'Mensagem contém palavra',
    inbox_changed: 'Cliente entrou na caixa',
    webhook_received: 'Webhook recebido',
  },
  linkTypes: {
    conversations: 'Conversas',
    contacts: 'Contatos',
    campaigns: 'Campanhas WhatsApp',
    proposals: 'Propostas',
    calendar: 'Agenda',
    integrations: 'Integrações',
    none: 'Sem atalho',
  },
  labels: {
    selectedNode: 'Nó selecionado',
    selectedEdge: 'Conexão selecionada',
    flowStart: 'Início do fluxo',
    flowStartDescription:
      'Use um bloco Entrada para definir o gatilho real, como cliente com etiqueta X, e conecte ao próximo passo.',
    edgeDefault: 'Conexão',
    edgeLabel: 'Rótulo',
    edgePlaceholder: 'Ex.: sem resposta, aprovado, oferta',
    conversionShort: 'Conv.',
    potentialShort: 'Pot.',
    connectorStart: 'Saída',
    connectorEnd: 'Entrada',
    connectorStartTitle: 'Iniciar conexão a partir deste nó',
    connectorEndTitle: 'Conectar neste nó',
    connectIdle: 'Clique na saída do bloco origem',
    connectActive: 'Agora clique na entrada do bloco destino',
    noInbox: 'Todas as caixas',
    active: 'Ativo',
    inactive: 'Inativo',
    uploadReady: 'Arquivo pronto para envio',
    uploadError: 'Não foi possível enviar o arquivo',
    labelField: 'Etiqueta',
    labelPlaceholder: 'ex.: aguardando_contrato',
    targetInbox: 'Caixa destino',
    triggerSettings: 'Quando',
    actionSettings: 'Então',
    triggerLabelPlaceholder: 'ex.: cliente_vip',
    triggerKeywordPlaceholder: 'ex.: quero simular',
    keyboardHint: 'Delete ou Backspace remove o item selecionado.',
    testRun: 'Teste de execução',
    testStepsOk: 'etapa(s) validada(s)',
    testStepsError: 'precisa(m) de ajuste',
    testStepOk: 'Validada',
    testStepError: 'Precisa de ajuste',
    testIdle: 'Clique em Testar fluxo para simular o caminho da automação.',
    testStarted: 'Entrada disparada',
    testFinished: 'Fluxo finalizado',
    noTriggerToTest: 'Adicione ou selecione uma Entrada para testar.',
    fileCaptionPlaceholder: 'ex.: segue o contrato para análise',
    webhookPlaceholder: 'https://exemplo.com/webhook',
    jsonPathPlaceholder: 'ex.: cliente.telefone',
    jsonDetectedFields: 'Campos detectados',
    jsonLastFields: 'Campos do último JSON',
    webhookCopied: 'URL copiada',
    webhookCopyFailed: 'Não foi possível copiar a URL.',
    webhookNoPayload: 'Nenhum JSON recebido ainda',
    noCreditEngine: 'Selecione um motor',
    noCreditEngines: 'Nenhum motor de crédito cadastrado.',
    creditEngineHelp:
      'O motor avalia os dados do contato, da conversa, da proposta e do webhook. Use nas conexões os rótulos “elegível”, “inelegível” e “dados incompletos”.',
    openCreditEngine: 'Revisar motor de crédito',
    inputColumn: 'Entrada',
    processColumn: 'Processos',
    outputColumn: 'Saída',
    testRunning: 'Executando',
    testSuccess: 'Executado',
    testError: 'Erro',
    testIncomplete: 'Configuração incompleta',
    testModalDescription:
      'Simulação visual: nenhum envio real é disparado durante o teste.',
    webhookWaiting: 'Aguardando entrada pelo webhook',
    webhookWaitingDetail:
      'Envie uma requisição para a URL de entrada. O teste inicia automaticamente quando o JSON chegar.',
    webhookReceived: 'Webhook recebido',
    webhookWaitTimeout:
      'Nenhum webhook recebido no tempo de espera. Envie o JSON e tente novamente.',
    outboundWebhookUnavailable:
      'Webhook de saída ainda não está habilitado com segurança. Substitua esta ação antes de ativar o fluxo.',
    nextNodeTitle: 'O que acontece depois?',
    nextNodeDescription: 'Escolha uma etapa para conectar após este nó.',
    nextNodeSearch: 'Buscar ação...',
    jsonVariablesHint:
      'Use os campos do webhook como variáveis, por exemplo {{cliente.nome}}.',
    removeNodeConfirm:
      'Excluir este nó e todas as conexões ligadas a ele? Esta ação não pode ser desfeita.',
    editorSubtitle: 'Configure o que este nó faz dentro do funil.',
    thisNode: 'Este nó',
    sectionIdentity: 'Identificação',
    sectionIdentityHelp: 'Nome, descrição e cor exibidos no card do canvas.',
    sectionBehavior: 'Comportamento',
    sectionBehaviorHelp:
      'Defina o tipo do nó e o que ele faz quando o fluxo passa por aqui.',
    sectionShortcut: 'Atalho e origem',
    sectionShortcutHelp:
      'Origem do lead e a tela do Talkvia aberta pelo atalho do card.',
    sectionMetrics: 'Métricas',
    sectionMetricsHelp:
      'Contadores reais do período e estimativas manuais opcionais; não alteram a automação.',
    conditionSettings: 'Regras da condição',
    previousNode: 'Nó anterior',
    nextNode: 'Próximo nó',
    goToNode: 'Ir para o nó',
    goToNodeSearch: 'Buscar nó...',
    chooseNode: 'Escolha o nó',
    noPreviousNode: 'Sem nó anterior',
    noNextNode: 'Sem próximo nó',
    navigationHint: 'Alt + ← / → navega entre os nós.',
    pendingTitle: 'Alterações ainda não salvas',
    pendingDescription:
      'As alterações deste nó continuam no fluxo, mas ainda não foram salvas. Deseja salvar agora e abrir o outro nó?',
    pendingConfirm: 'Salvar e abrir',
    pendingSaveError:
      'Não foi possível salvar agora. As alterações continuam no fluxo; tente novamente.',
    edgeEditorTitle: 'Ligação',
    edgeEditorDescription:
      'O rótulo define por qual resultado do nó de origem esta ligação é seguida.',
    edgeBranches: 'Caminhos deste nó',
    edgeCustomLabel: 'Rótulo personalizado',
    removeEdge: 'Remover ligação',
    edgeNoBranchHint:
      'Este nó segue por todas as ligações; o rótulo é apenas descritivo.',
    flowInbox: 'Caixa WhatsApp Cloud',
    flowInboxPlaceholder: 'Selecione a caixa',
    flowSelect: 'Flow publicado',
    flowSelectPlaceholder: 'Selecione um Flow',
    flowSearch: 'Buscar Flow...',
    flowEmpty: 'Nenhum Flow disponível nesta caixa.',
    flowNoCloudInbox:
      'Conecte uma caixa WhatsApp Cloud para enviar Flows pelo funil.',
    flowDraftWarning:
      'Este Flow é um rascunho: ele só pode ser enviado em modo de teste e não deve ser usado com clientes. Publique-o em WhatsApp Flows antes de ativar o funil.',
    flowDraftConfirm: 'Enviar em modo de teste (rascunho)',
    flowStatusDraft: 'Rascunho (teste)',
    flowCta: 'Texto do botão',
    flowCtaPlaceholder: 'ex.: Preencher cadastro',
    flowBody: 'Texto da mensagem',
    flowBodyPlaceholder: 'ex.: Preencha o formulário para continuarmos.',
    flowHeader: 'Cabeçalho (opcional)',
    flowFooter: 'Rodapé (opcional)',
    flowScreen: 'Tela inicial',
    flowScreenHelp: 'Preenchida automaticamente com a primeira tela do Flow.',
    flowRules:
      'O Flow só é enviado quando a conversa estiver na caixa selecionada e dentro da janela de 24 horas do WhatsApp. Fora dela, ou para contatos com etiqueta de bloqueio, a etapa não envia e registra o motivo. Use os rótulos “enviado” e “não enviado” nas conexões para seguir caminhos diferentes.',
    flowNotFound: 'O Flow selecionado não foi encontrado nesta caixa.',
    sendMessageTarget: 'Destino da mensagem',
    sendMessageTargetHelp:
      'Quando o fluxo começa por webhook, o telefone abaixo define qual contato recebe a mensagem.',
    selectPlaceholder: 'Selecione',
    searchPlaceholder: 'Buscar...',
    noResults: 'Nenhum resultado encontrado.',
    labelSelectPlaceholder: 'Selecione a etiqueta',
    labelSearch: 'Buscar etiqueta...',
    inboxSearch: 'Buscar caixa...',
    noLabels: 'Nenhuma etiqueta cadastrada nesta conta.',
    teamField: 'Equipe',
    teamPlaceholder: 'Selecione a equipe',
    teamSearch: 'Buscar equipe...',
    noTeams: 'Nenhuma equipe cadastrada nesta conta.',
    priorityField: 'Prioridade',
    priorityPlaceholder: 'Selecione a prioridade',
    privateNoteField: 'Texto da nota privada',
    privateNotePlaceholder: 'ex.: Cliente pediu retorno amanhã.',
    nodeShortcutSearch: 'Buscar nó...',
    nodeShortcutEmpty: 'Nenhum nó encontrado para a busca.',
    dragConnectHint:
      'Arraste o + de um nó até outro para ligar. Clique no + para escolher a próxima etapa.',
    dragConnectTarget: 'Solte para ligar',
  },
  empty: {
    panel:
      'Selecione um nó para editar detalhes, duplicar ou abrir o atalho vinculado.',
    canvas:
      'Arraste os nós pelo canvas. Arraste o + de um nó até outro, ou use Conectar e clique em dois nós, para criar uma ligação.',
  },
};

const nodeTypes = [
  { value: 'trigger', label: copy.nodeTypes.trigger, icon: 'i-lucide-play' },
  { value: 'action', label: copy.nodeTypes.action, icon: 'i-lucide-zap' },
  {
    value: 'decision',
    label: copy.nodeTypes.decision,
    icon: 'i-lucide-git-branch',
  },
  { value: 'wait', label: copy.nodeTypes.wait, icon: 'i-lucide-clock-3' },
  {
    value: 'offer',
    label: copy.nodeTypes.offer,
    icon: 'i-lucide-badge-dollar-sign',
  },
  { value: 'end', label: copy.nodeTypes.end, icon: 'i-lucide-flag' },
];

const cardPalette = [
  { value: 'emerald', label: 'Verde' },
  { value: 'blue', label: 'Azul' },
  { value: 'amber', label: 'Amarelo' },
  { value: 'ruby', label: 'Vermelho' },
  { value: 'violet', label: 'Violeta' },
  { value: 'slate', label: 'Cinza' },
];

const actionOptions = [
  { value: 'none', label: copy.actionTypes.none },
  { value: 'send_message', label: copy.actionTypes.send_message },
  { value: 'send_file', label: copy.actionTypes.send_file },
  { value: 'add_label', label: copy.actionTypes.add_label },
  { value: 'move_inbox', label: copy.actionTypes.move_inbox },
  { value: 'request_best_offer', label: copy.actionTypes.request_best_offer },
  { value: 'create_contract', label: copy.actionTypes.create_contract },
  { value: 'assign_human', label: copy.actionTypes.assign_human },
  { value: 'run_credit_engine', label: copy.actionTypes.run_credit_engine },
  { value: 'send_whatsapp_flow', label: copy.actionTypes.send_whatsapp_flow },
  { value: 'add_private_note', label: copy.actionTypes.add_private_note },
  { value: 'assign_team', label: copy.actionTypes.assign_team },
  { value: 'set_priority', label: copy.actionTypes.set_priority },
  { value: 'remove_label', label: copy.actionTypes.remove_label },
  { value: 'http_request', label: copy.actionTypes.http_request },
];

const triggerOptions = [
  {
    value: 'conversation_created',
    label: copy.triggerTypes.conversation_created,
  },
  { value: 'label_added', label: copy.triggerTypes.label_added },
  { value: 'message_contains', label: copy.triggerTypes.message_contains },
  { value: 'inbox_changed', label: copy.triggerTypes.inbox_changed },
  { value: 'webhook_received', label: copy.triggerTypes.webhook_received },
];

const webhookMethods = ['POST', 'PUT', 'PATCH', 'GET'];

const createWebhookToken = () => {
  const cryptoApi = window.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const randomPart = Math.random().toString(36).slice(2, 14);
  return `tw-${Date.now()}-${randomPart}`;
};

const linkOptions = [
  { value: 'conversations', label: copy.linkTypes.conversations },
  { value: 'contacts', label: copy.linkTypes.contacts },
  { value: 'campaigns', label: copy.linkTypes.campaigns },
  { value: 'proposals', label: copy.linkTypes.proposals },
  { value: 'calendar', label: copy.linkTypes.calendar },
  { value: 'integrations', label: copy.linkTypes.integrations },
  { value: 'none', label: copy.linkTypes.none },
];

const emptyNodeAction = () => ({
  type: 'none',
  message: '',
  label: '',
  targetInboxId: '',
  delayMinutes: 0,
  file: null,
  fileCaption: '',
  webhookUrl: '',
  webhookMethod: 'POST',
  creditEngineId: '',
  contactPath: '',
  phonePath: '',
  messagePath: '',
  flowInboxId: '',
  flowId: '',
  flowBody: '',
  flowCta: '',
  flowHeader: '',
  flowFooter: '',
  flowScreen: '',
  flowMode: 'published',
  teamId: '',
  priority: '',
  httpUrl: '',
  httpMethod: 'POST',
  httpHeaders: [],
  httpBody: '',
  httpTimeout: 10,
  saveAs: 'http',
  lastTestSample: null,
  condition: null,
});

const normalizeNodeAction = action => ({
  ...emptyNodeAction(),
  ...(action && typeof action === 'object' ? action : {}),
});

const emptyNodeTrigger = () => ({
  type: 'conversation_created',
  label: '',
  inboxId: '',
  keyword: '',
  webhookMethod: 'POST',
  webhookToken: createWebhookToken(),
  webhookSample:
    '{\n  "cliente": {\n    "nome": "Maria",\n    "telefone": "5511999999999"\n  },\n  "mensagem": "quero simular"\n}',
  contactPath: 'cliente.nome',
  phonePath: 'cliente.telefone',
  messagePath: 'mensagem',
  lastPayload: null,
  lastFields: [],
  lastReceivedAt: '',
  lastRequest: null,
});

const normalizeNodeTrigger = trigger => ({
  ...emptyNodeTrigger(),
  ...(trigger && typeof trigger === 'object' ? trigger : {}),
});

const makeNode = payload => ({
  id:
    payload.id ||
    `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type: payload.type || 'action',
  title: payload.title || 'Novo nó',
  description: payload.description || '',
  source: payload.source || '',
  leads: Number(payload.leads || 0),
  investment: Number(payload.investment || 0),
  conversion: Number(payload.conversion || 0),
  potential: Number(payload.potential || 0),
  color: payload.color || 'blue',
  linkType: payload.linkType || payload.link_type || 'conversations',
  action: normalizeNodeAction(payload.action || payload.metadata?.action),
  trigger: normalizeNodeTrigger(payload.trigger || payload.metadata?.trigger),
  position: {
    x: Number(payload.position?.x ?? payload.x ?? 120),
    y: Number(payload.position?.y ?? payload.y ?? 120),
  },
});

const defaultFlow = () => ({
  name: 'Funil de Vendas - Compass',
  nodes: [
    makeNode({
      id: 'instagram-ads',
      type: 'trigger',
      title: 'Instagram Ads',
      description: 'Campanhas pagas enviando leads para WhatsApp.',
      source: 'Meta Ads',
      color: 'blue',
      linkType: 'campaigns',
      trigger: { type: 'label_added', label: 'lead_meta_ads' },
      position: { x: 80, y: 120 },
    }),
    makeNode({
      id: 'organico',
      type: 'trigger',
      title: 'Instagram orgânico',
      description: 'Conteúdo, Reels e Stories conectados ao inbox.',
      source: 'Instagram',
      color: 'emerald',
      linkType: 'conversations',
      trigger: { type: 'conversation_created' },
      position: { x: 80, y: 330 },
    }),
    makeNode({
      id: 'lead-scoring',
      type: 'decision',
      title: 'Lead scoring',
      description: 'Classificação automática para priorizar pré-venda.',
      source: 'Qualifica AI',
      color: 'amber',
      linkType: 'contacts',
      position: { x: 500, y: 220 },
    }),
    makeNode({
      id: 'followup',
      type: 'wait',
      title: 'Follow-up automático',
      description: 'Retomada pelo WhatsApp quando o cliente fica inativo.',
      source: 'Talkvia Inbox',
      color: 'violet',
      linkType: 'conversations',
      position: { x: 920, y: 220 },
    }),
    makeNode({
      id: 'oferta',
      type: 'offer',
      title: 'Oferta de melhor condição',
      description: 'Busca a tabela mais favorável ou usa a escolha manual.',
      source: 'Integração bancária',
      color: 'emerald',
      linkType: 'integrations',
      position: { x: 1340, y: 220 },
    }),
  ],
  edges: [
    {
      id: 'edge-ads-score',
      source: 'instagram-ads',
      target: 'lead-scoring',
      label: 'lead',
    },
    {
      id: 'edge-org-score',
      source: 'organico',
      target: 'lead-scoring',
      label: 'lead',
    },
    {
      id: 'edge-score-follow',
      source: 'lead-scoring',
      target: 'followup',
      label: 'sem resposta',
    },
    {
      id: 'edge-follow-offer',
      source: 'followup',
      target: 'oferta',
      label: 'x tempo',
    },
  ],
  viewport: { x: 24, y: 36, zoom: 0.85 },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const flow = reactive(defaultFlow());
const automationSettings = reactive({ inboxId: '', active: true });
const funnels = ref([]);
const selectedNodeId = ref('');
const selectedEdgeId = ref('');
const currentFunnelId = ref(null);
const creditEngines = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const saveError = ref(false);
const hasLoadedRemoteFlow = ref(false);
const isConnecting = ref(false);
const connectionSourceId = ref('');
const canvasEl = ref(null);
const dragState = ref(null);
const panState = ref(null);
const isUploadingFile = ref(false);
const uploadError = ref(false);
const contextMenu = reactive({
  visible: false,
  nodeId: '',
  x: 0,
  y: 0,
});
const nodePicker = reactive({
  visible: false,
  sourceNodeId: '',
  search: '',
});
const panelShortcutSearch = ref('');
// Ligação por arraste a partir do "+" (coordenadas do canvas, sem zoom).
const linkDrag = reactive({
  active: false,
  sourceId: '',
  originClient: { x: 0, y: 0 },
  pointer: { x: 0, y: 0 },
  moved: false,
  targetId: '',
});
let suppressHandleClick = false;
const isNodeInspectorOpen = ref(false);
const isConfirmingNodeRemoval = ref(false);
const whatsappFlows = ref([]);
const isLoadingWhatsappFlows = ref(false);
const whatsappFlowsLoaded = ref(false);
const isFlowPanelCollapsed = ref(false);
// Contadores reais (GET talkvia_funnels/:id/metrics); abrir a tela só lê.
const flowMetrics = ref(null);
const metricsPeriod = ref(30);
const isLoadingMetrics = ref(false);
let metricsRequestId = 0;
const edgeEditor = reactive({ visible: false, edgeId: '' });
const breadcrumbMenu = ref('');
const pendingNavigationTarget = ref('');
const pendingNavigationDialog = ref(null);
const isSavingBeforeNavigation = ref(false);
const hasPendingSave = ref(false);
let inspectorSnapshot = '';
const testRun = reactive({
  activeNodeId: '',
  isRunning: false,
  isModalOpen: false,
  logs: [],
  statusByNode: {},
});
let saveTimer = null;
let testTimers = [];
let webhookWaitTimer = null;
let webhookWaitStartedAt = null;

const selectedNode = computed(() =>
  flow.nodes.find(node => node.id === selectedNodeId.value)
);

const contextMenuNode = computed(() =>
  flow.nodes.find(node => node.id === contextMenu.nodeId)
);

const inboxes = useMapGetter('inboxes/getAllInboxes');
const currentUserId = useMapGetter('getCurrentUserID');
const currentRole = useMapGetter('getCurrentRole');
const globalConfig = useMapGetter('globalConfig/get');
const attributesByModel = useMapGetter('attributes/getAttributesByModel');
const countersKey = computed(() =>
  countersPreferenceKey(currentUserId.value, route.params.accountId)
);
const showNodeCounters = ref(readCountersPreference(countersKey.value));
const canTestHttpRequest = computed(
  () => currentRole.value === 'administrator'
);
const maxUploadSizeMb = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);
const contactAttributes = computed(() =>
  typeof attributesByModel.value === 'function'
    ? attributesByModel.value('contact_attribute') || []
    : []
);
const accountLabels = useMapGetter('labels/getLabels');
const accountTeams = useMapGetter('teams/getTeams');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const CORBAN_FEATURES = [
  FEATURE_FLAGS.TALKVIA_CORBAN,
  FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY,
  FEATURE_FLAGS.TALKVIA_CORBAN_BENEFIT360,
  FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS,
  FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE,
];
// Blocos de crédito (motor, melhor oferta, contrato) só aparecem para Corban.
const hasCorban = computed(() => {
  const isEnabled = isFeatureEnabledonAccount.value;
  if (typeof isEnabled !== 'function') return false;
  const accountId = Number(route.params.accountId);
  return CORBAN_FEATURES.some(feature => isEnabled(accountId, feature));
});
const creditEngineOptions = computed(() =>
  (creditEngines.value || [])
    .filter(engine => engine.active !== false)
    .map(engine => ({
      value: Number(engine.id),
      label: [engine.bank_name, engine.name, engine.product_type]
        .filter(Boolean)
        .join(' · '),
    }))
);

const selectedCreditEngine = computed(() =>
  creditEngines.value.find(
    engine =>
      Number(engine.id) === Number(selectedNode.value?.action?.creditEngineId)
  )
);
const selectedCreditEngineLabel = computed(() =>
  [selectedCreditEngine.value?.bank_name, selectedCreditEngine.value?.name]
    .filter(Boolean)
    .join(' · ')
);

const inboxOptions = computed(() =>
  (inboxes.value || [])
    .filter(inbox => inbox?.id)
    .map(inbox => ({
      value: Number(inbox.id),
      label: inbox.name || `Caixa ${inbox.id}`,
    }))
);

// O getter getAllInboxes devolve chaves em camelCase (channelType); aceitar
// também snake_case para não depender do formato do store.
const isWhatsappCloudInbox = inbox =>
  (inbox?.channelType || inbox?.channel_type) === 'Channel::Whatsapp' &&
  inbox?.provider === 'whatsapp_cloud';

const cloudInboxOptions = computed(() =>
  (inboxes.value || [])
    .filter(inbox => inbox?.id && isWhatsappCloudInbox(inbox))
    .map(inbox => ({
      value: Number(inbox.id),
      label: inbox.name || `Caixa ${inbox.id}`,
    }))
);

const actionTypeOptions = computed(() => {
  const currentType = selectedNode.value?.action?.type;
  return actionOptions.filter(
    option =>
      hasCorban.value ||
      !isCorbanActionType(option.value) ||
      option.value === currentType
  );
});

const labelOptions = computed(() =>
  (accountLabels.value || [])
    .filter(label => label?.title)
    .map(label => ({ value: label.title, label: label.title }))
);

const teamOptions = computed(() =>
  (accountTeams.value || [])
    .filter(team => team?.id)
    .map(team => ({
      value: Number(team.id),
      label: team.name || `Equipe ${team.id}`,
    }))
);

const optionLabel = (options, value) =>
  options.find(option => String(option.value) === String(value))?.label || '';

const inboxOptionsWithAll = computed(() => [
  { value: '', label: copy.labels.noInbox },
  ...inboxOptions.value,
]);

// ComboBox emite '' ao clicar de novo na opção atual; campos obrigatórios
// ignoram esse valor e mantêm o rótulo pelo display-label.
const idValue = value =>
  value === '' || value === null || value === undefined ? '' : Number(value);

const assignWhenSelected = (target, key, value) => {
  if (!target || value === '' || value === null || value === undefined) return;
  target[key] = value;
};

const automationStatusValue = computed({
  get: () => (automationSettings.active === false ? 'inactive' : 'active'),
  set: value => {
    if (!value) return;
    automationSettings.active = value !== 'inactive';
  },
});

const automationStatusOptions = [
  { value: 'active', label: copy.labels.active },
  { value: 'inactive', label: copy.labels.inactive },
];

const webhookMethodOptions = webhookMethods.map(method => ({
  value: method,
  label: method,
}));

const sendableWhatsappFlowStatuses = ['PUBLISHED', 'DRAFT'];

const whatsappFlowById = flowId =>
  whatsappFlows.value.find(item => Number(item.id) === Number(flowId));

const selectedWhatsappFlow = computed(() =>
  selectedNode.value?.action?.type === 'send_whatsapp_flow'
    ? whatsappFlowById(selectedNode.value.action.flowId)
    : null
);

const selectedWhatsappFlowOptions = computed(() => {
  const inboxId = Number(selectedNode.value?.action?.flowInboxId);
  if (!inboxId) return [];
  return whatsappFlows.value
    .filter(
      item =>
        Number(item.inbox_id) === inboxId &&
        sendableWhatsappFlowStatuses.includes(item.status)
    )
    .map(item => ({
      value: Number(item.id),
      label:
        item.status === 'DRAFT'
          ? `${item.name} · ${copy.labels.flowStatusDraft}`
          : item.name,
    }));
});

const loadWhatsappFlows = async () => {
  if (isLoadingWhatsappFlows.value) return;
  isLoadingWhatsappFlows.value = true;
  try {
    const { data } = await WhatsappFlowsAPI.list();
    whatsappFlows.value = data.payload || [];
  } catch {
    whatsappFlows.value = [];
  } finally {
    isLoadingWhatsappFlows.value = false;
    whatsappFlowsLoaded.value = true;
  }
};

const ensureWhatsappFlowsLoaded = () => {
  if (whatsappFlowsLoaded.value || !cloudInboxOptions.value.length) return;
  loadWhatsappFlows();
};

const selectWhatsappFlowInbox = inboxId => {
  if (!selectedNode.value) return;
  const action = normalizeNodeAction(selectedNode.value.action);
  if (Number(action.flowInboxId) === Number(inboxId)) return;
  selectedNode.value.action = {
    ...action,
    flowInboxId: inboxId ? Number(inboxId) : '',
    flowId: '',
    flowScreen: '',
    flowMode: 'published',
  };
};

const selectWhatsappFlow = flowId => {
  if (!selectedNode.value) return;
  const flowRecord = whatsappFlowById(flowId);
  selectedNode.value.action = {
    ...normalizeNodeAction(selectedNode.value.action),
    flowId: flowRecord ? Number(flowRecord.id) : '',
    flowScreen: flowRecord?.first_screen_id || '',
    // Rascunhos exigem confirmação explícita do modo de teste.
    flowMode: 'published',
  };
};

const toggleWhatsappFlowDraftMode = event => {
  if (!selectedNode.value) return;
  selectedNode.value.action.flowMode = event.target.checked
    ? 'draft'
    : 'published';
};

const openWhatsappFlowsPage = () => {
  router.push({
    name: 'whatsapp_flows',
    params: { accountId: route.params.accountId },
    query: selectedNode.value?.action?.flowInboxId
      ? { inbox_id: selectedNode.value.action.flowInboxId }
      : {},
  });
};

const parseJsonSample = value => {
  try {
    return JSON.parse(value || '{}');
  } catch {
    return null;
  }
};

const flattenJsonPaths = (value, prefix = '') => {
  if (!value || typeof value !== 'object') return [];

  if (Array.isArray(value)) {
    return value.flatMap((childValue, index) => {
      const path = prefix ? `${prefix}[${index}]` : `items[${index}]`;
      if (childValue && typeof childValue === 'object') {
        return flattenJsonPaths(childValue, path);
      }
      return [path];
    });
  }

  return Object.entries(value).flatMap(([key, childValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (childValue && typeof childValue === 'object') {
      return flattenJsonPaths(childValue, path);
    }
    return [path];
  });
};

const jsonPathParts = path =>
  String(path || '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

const valueFromJsonPath = (payload, path) => {
  if (!payload || !path) return '';
  return (
    jsonPathParts(path).reduce((value, key) => value?.[key], payload) ?? ''
  );
};

const selectedWebhookFields = computed(() => {
  if (selectedNode.value?.type !== 'trigger') return [];
  const payload = parseJsonSample(selectedNode.value.trigger?.webhookSample);
  return flattenJsonPaths(payload).slice(0, 12);
});

const selectedWebhookLastFields = computed(() => {
  const fields = selectedNode.value?.trigger?.lastFields;
  if (!Array.isArray(fields)) return [];
  return fields
    .map(field => (typeof field === 'string' ? { path: field } : field))
    .filter(field => field?.path)
    .slice(0, 16);
});

const selectedWebhookFieldOptions = computed(() =>
  selectedWebhookLastFields.value.length
    ? selectedWebhookLastFields.value.map(field => field.path)
    : selectedWebhookFields.value
);

const selectedWebhookPayloadPreview = computed(() => {
  const payload = selectedNode.value?.trigger?.lastPayload;
  if (!payload) return '';
  return JSON.stringify(payload, null, 2);
});

const sourceNodesFor = node => {
  if (!node) return [];
  return flow.edges
    .filter(edge => edge.target === node.id)
    .map(edge => flow.nodes.find(item => item.id === edge.source))
    .filter(Boolean);
};

const upstreamWebhookTriggerFor = node => {
  const queue = sourceNodesFor(node);
  const visited = new Set();

  while (queue.length) {
    const current = queue.shift();
    if (current && !visited.has(current.id)) {
      visited.add(current.id);

      if (
        current.type === 'trigger' &&
        normalizeNodeTrigger(current.trigger).type === 'webhook_received'
      ) {
        return current;
      }

      queue.push(...sourceNodesFor(current));
    }
  }

  return null;
};

const webhookFieldOptionsFor = triggerNode => {
  if (!triggerNode) return [];
  const fields = triggerNode.trigger?.lastFields;
  if (Array.isArray(fields) && fields.length) {
    return fields
      .map(field => (typeof field === 'string' ? field : field?.path))
      .filter(Boolean)
      .slice(0, 16);
  }

  return flattenJsonPaths(parseJsonSample(triggerNode.trigger?.webhookSample))
    .filter(Boolean)
    .slice(0, 16);
};

const selectedActionWebhookTrigger = computed(() =>
  selectedNode.value?.type === 'trigger'
    ? null
    : upstreamWebhookTriggerFor(selectedNode.value)
);

const selectedActionWebhookFieldOptions = computed(() =>
  webhookFieldOptionsFor(selectedActionWebhookTrigger.value)
);

const nextNodeShortcutGroups = computed(() =>
  groupNodeShortcuts(
    filterNodeShortcuts(
      availableNodeShortcuts({ hasCorban: hasCorban.value, context: 'next' }),
      nodePicker.search
    )
  )
);

const panelShortcutGroups = computed(() =>
  groupNodeShortcuts(
    filterNodeShortcuts(
      availableNodeShortcuts({ hasCorban: hasCorban.value }),
      panelShortcutSearch.value
    )
  )
);

const projectOptions = computed(() =>
  funnels.value.map(funnel => ({
    value: funnel.id,
    label: funnel.name || `Projeto ${funnel.id}`,
  }))
);

const nodeCount = computed(() => flow.nodes.length);
const edgeCount = computed(() => flow.edges.length);
const totalLeadsLabel = computed(() =>
  formatMetricCount(flowMetrics.value?.totals?.unique_leads)
);
const averageConversionLabel = computed(() =>
  formatMetricPercent(flowMetrics.value?.totals?.average_conversion)
);
const metricsPeriodLabel = computed(
  () =>
    METRIC_PERIODS.find(period => period.value === metricsPeriod.value)
      ?.label || ''
);

const loadFlowMetrics = async () => {
  const funnelId = currentFunnelId.value;
  metricsRequestId += 1;
  const requestId = metricsRequestId;
  if (!funnelId || typeof TalkviaFunnelsAPI.metrics !== 'function') {
    flowMetrics.value = null;
    return;
  }
  isLoadingMetrics.value = true;
  try {
    const { data } = await TalkviaFunnelsAPI.metrics(funnelId, {
      days: metricsPeriod.value,
    });
    if (requestId === metricsRequestId) flowMetrics.value = data;
  } catch {
    if (requestId === metricsRequestId) {
      flowMetrics.value = null;
      useAlert(copy.metrics.loadError);
    }
  } finally {
    if (requestId === metricsRequestId) isLoadingMetrics.value = false;
  }
};

const nodeMetrics = node => nodeMetricsFor(flowMetrics.value, node?.id);

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const nodeCounterItems = node => {
  const metrics = nodeMetrics(node);
  const items = [
    {
      key: 'leads',
      label: copy.metrics.nodeLeads,
      value: formatMetricCount(
        metrics?.leads ?? (flowMetrics.value ? 0 : null)
      ),
      help: copy.metrics.nodeLeadsHelp,
    },
    {
      key: 'conversion',
      label: copy.metrics.nodeConversion,
      value: formatMetricPercent(metrics?.conversion),
      help: copy.metrics.nodeConversionHelp,
    },
  ];
  if (hasPotentialEstimate(node)) {
    items.push({
      key: 'potential',
      label: copy.metrics.nodePotential,
      value: `+${moneyFormatter.format(Number(node.potential))}`,
      help: copy.metrics.nodePotentialHelp,
    });
  }
  return items;
};

const toggleNodeCounters = value => {
  showNodeCounters.value = Boolean(value);
  writeCountersPreference(countersKey.value, showNodeCounters.value);
};

const selectMetricsPeriod = value => {
  if (!value || Number(value) === metricsPeriod.value) return;
  metricsPeriod.value = Number(value);
  loadFlowMetrics();
};

const selectedUpstreamHttpNodes = computed(() =>
  selectedNode.value
    ? upstreamHttpNodes(flow.nodes, flow.edges, selectedNode.value.id)
    : []
);
const selectedUpstreamHttpVariables = computed(() =>
  httpVariablesFromNodes(selectedUpstreamHttpNodes.value)
);
const selectedHttpSamples = computed(() =>
  httpContextSamples(selectedUpstreamHttpNodes.value)
);
const selectedConditionFieldOptions = computed(() =>
  conditionFieldOptions({
    contactAttributes: contactAttributes.value,
    httpVariables: selectedUpstreamHttpVariables.value,
  })
);

const patchSelectedAction = patch => {
  if (!selectedNode.value || !patch) return;
  selectedNode.value.action = {
    ...normalizeNodeAction(selectedNode.value.action),
    ...patch,
  };
};

const updateSelectedCondition = condition =>
  patchSelectedAction({ condition: normalizeCondition(condition) });

const showsActionSelector = node =>
  node?.type !== 'decision' || normalizeNodeAction(node.action).type !== 'none';

const saveStatusLabel = computed(() => {
  if (isLoading.value) return copy.status.loading;
  if (saveError.value) return copy.status.error;
  if (isSaving.value) return copy.status.saving;
  return copy.status.saved;
});

const saveStatusClass = computed(() => {
  if (saveError.value) return 'text-ruby-700';
  if (isSaving.value || isLoading.value) return 'text-amber-700';
  return 'text-emerald-700';
});

const canvasStyle = computed(() => ({
  transform: `translate(${flow.viewport.x}px, ${flow.viewport.y}px) scale(${flow.viewport.zoom})`,
}));

const zoomLabel = computed(() => `${Math.round(flow.viewport.zoom * 100)}%`);

const formattedCurrency = value => moneyFormatter.format(Number(value || 0));

const parseCurrency = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0) / 100;
};

const selectedNodeInvestmentDisplay = computed({
  get: () =>
    selectedNode.value ? formattedCurrency(selectedNode.value.investment) : '',
  set: value => {
    if (selectedNode.value)
      selectedNode.value.investment = parseCurrency(value);
  },
});

const selectedNodePotentialDisplay = computed({
  get: () =>
    selectedNode.value ? formattedCurrency(selectedNode.value.potential) : '',
  set: value => {
    if (selectedNode.value) selectedNode.value.potential = parseCurrency(value);
  },
});

const nodeTypeMeta = type =>
  nodeTypes.find(item => item.value === type) || nodeTypes[1];

const nodeColorForType = type => {
  if (type === 'offer') return 'emerald';
  if (type === 'decision') return 'amber';
  return 'blue';
};

const nodeToneClass = color => {
  const tones = {
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    blue: 'border-blue-300 bg-blue-50 text-blue-800',
    amber: 'border-amber-300 bg-amber-50 text-amber-800',
    ruby: 'border-ruby-300 bg-ruby-50 text-ruby-800',
    violet: 'border-violet-300 bg-violet-50 text-violet-800',
    slate: 'border-n-weak bg-n-alpha-2 text-n-slate-11',
  };
  return tones[color] || tones.slate;
};

const triggerTypeLabel = type =>
  triggerOptions.find(option => option.value === type)?.label ||
  copy.triggerTypes.conversation_created;

const triggerSummary = node => {
  const trigger = normalizeNodeTrigger(node?.trigger);
  if (trigger.type === 'label_added' && trigger.label) {
    return `${copy.triggerTypes.label_added}: ${trigger.label}`;
  }
  if (trigger.type === 'message_contains' && trigger.keyword) {
    return `${copy.triggerTypes.message_contains}: ${trigger.keyword}`;
  }
  if (trigger.type === 'inbox_changed' && trigger.inboxId) {
    const inbox = inboxOptions.value.find(
      item => Number(item.value) === Number(trigger.inboxId)
    );
    return `${copy.triggerTypes.inbox_changed}: ${inbox?.label || trigger.inboxId}`;
  }
  if (trigger.type === 'webhook_received') {
    return `${copy.triggerTypes.webhook_received}: ${trigger.webhookMethod || 'POST'}`;
  }
  return triggerTypeLabel(trigger.type);
};

const webhookEndpointFor = node => {
  const accountId = route.params.accountId;
  const funnelId = currentFunnelId.value || 'novo';
  const nodeId = node?.id || 'entrada';
  const token = node?.trigger?.webhookToken || 'token';
  const path = `/webhooks/talkvia/funnels/${accountId}/${funnelId}/${nodeId}/${token}`;
  return `${window.location.origin}${path}`;
};

const copyWebhookEndpoint = async node => {
  const endpoint = webhookEndpointFor(node);
  try {
    await copyTextToClipboard(endpoint);
    useAlert(copy.labels.webhookCopied);
  } catch {
    useAlert(copy.labels.webhookCopyFailed);
  }
};

const routeForLinkType = linkType => {
  const accountId = route.params.accountId;
  const routes = {
    conversations: { name: 'talkvia_conversations', params: { accountId } },
    contacts: { name: 'contacts_dashboard_index', params: { accountId } },
    campaigns: { name: 'campaigns_whatsapp_index', params: { accountId } },
    proposals: { name: 'talkvia_proposals', params: { accountId } },
    calendar: { name: 'talkvia_calendar', params: { accountId } },
    integrations: { name: 'settings_applications', params: { accountId } },
  };
  return routes[linkType];
};

const creditEngineLabel = engineId =>
  creditEngineOptions.value.find(
    item => Number(item.value) === Number(engineId)
  )?.label || copy.labels.noCreditEngine;

const openLinkedResource = node => {
  const targetRoute = routeForLinkType(node?.linkType);
  if (targetRoute) router.push(targetRoute);
};

const webhookSummary = action => {
  if (!action.webhookUrl) return copy.actionTypes.call_webhook;
  return `${action.webhookMethod || 'POST'} ${action.webhookUrl}`;
};

const actionSummary = node => {
  const action = normalizeNodeAction(node?.action);
  if (action.type === 'send_message') {
    return action.message || copy.actionTypes.send_message;
  }
  if (action.type === 'send_file') {
    return action.file?.name
      ? `${copy.actionTypes.send_file}: ${action.file.name}`
      : copy.actionTypes.send_file;
  }
  if (['add_label', 'remove_label'].includes(action.type)) {
    return action.label
      ? `${copy.actionTypes[action.type]}: ${action.label}`
      : copy.actionTypes[action.type];
  }
  if (action.type === 'assign_team') {
    const team = optionLabel(teamOptions.value, action.teamId);
    return team
      ? `${copy.actionTypes.assign_team}: ${team}`
      : copy.actionTypes.assign_team;
  }
  if (action.type === 'set_priority') {
    const priority = optionLabel(PRIORITY_OPTIONS, action.priority);
    return priority
      ? `${copy.actionTypes.set_priority}: ${priority}`
      : copy.actionTypes.set_priority;
  }
  if (action.type === 'add_private_note') {
    return action.message || copy.actionTypes.add_private_note;
  }
  if (action.type === 'move_inbox') {
    const inbox = inboxOptions.value.find(
      item => Number(item.value) === Number(action.targetInboxId)
    );
    return `${copy.actionTypes.move_inbox}: ${inbox?.label || copy.labels.noInbox}`;
  }
  if (action.type === 'call_webhook') return webhookSummary(action);
  if (action.type === 'http_request') {
    return action.httpUrl
      ? `${action.httpMethod || 'POST'} ${action.httpUrl}`
      : copy.actionTypes.http_request;
  }
  if (node?.type === 'decision' && activeRules(action.condition).length) {
    return conditionSummary(action.condition, conditionFieldOptions());
  }
  if (action.type === 'run_credit_engine') {
    return `${copy.actionTypes.run_credit_engine}: ${creditEngineLabel(action.creditEngineId)}`;
  }
  if (action.type === 'send_whatsapp_flow') {
    const flowRecord = whatsappFlowById(action.flowId);
    return flowRecord
      ? `${copy.actionTypes.send_whatsapp_flow}: ${flowRecord.name}`
      : copy.actionTypes.send_whatsapp_flow;
  }
  return copy.actionTypes[action.type] || copy.actionTypes.none;
};

const nodeExecutionSummary = node =>
  node?.type === 'trigger' ? triggerSummary(node) : actionSummary(node);

const nextNodeFrom = nodeId => {
  const nextEdge = flow.edges.find(edge => edge.source === nodeId);
  return flow.nodes.find(node => node.id === nextEdge?.target);
};

const executionPathFrom = startNode => {
  const path = [];
  const visited = new Set();
  let current = startNode;

  while (current && !visited.has(current.id) && path.length < 24) {
    path.push(current);
    visited.add(current.id);
    current = nextNodeFrom(current.id);
  }

  return path;
};

const clearTestRun = () => {
  testTimers.forEach(timer => window.clearTimeout(timer));
  testTimers = [];
  window.clearTimeout(webhookWaitTimer);
  webhookWaitTimer = null;
  webhookWaitStartedAt = null;
  testRun.activeNodeId = '';
  testRun.isRunning = false;
  testRun.logs = [];
  testRun.statusByNode = {};
};

const closeTestModal = () => {
  testRun.isModalOpen = false;
};

const validationResult = (isValid, message = '') => ({
  isValid,
  message:
    message || (isValid ? copy.labels.testSuccess : copy.labels.testIncomplete),
});

const validateTrigger = node => {
  const trigger = normalizeNodeTrigger(node?.trigger);
  if (trigger.type === 'label_added') {
    return validationResult(Boolean(trigger.label), copy.form.triggerLabel);
  }
  if (trigger.type === 'message_contains') {
    return validationResult(Boolean(trigger.keyword), copy.form.triggerKeyword);
  }
  if (trigger.type === 'inbox_changed') {
    return validationResult(Boolean(trigger.inboxId), copy.form.triggerInbox);
  }
  if (trigger.type === 'webhook_received') {
    const payload = parseJsonSample(trigger.webhookSample);
    if (!payload)
      return validationResult(false, copy.form.triggerWebhookSample);
    const hasAnyPath = [
      trigger.contactPath,
      trigger.phonePath,
      trigger.messagePath,
    ].some(path => valueFromJsonPath(payload, path));
    return validationResult(hasAnyPath, copy.labels.jsonDetectedFields);
  }
  return validationResult(true);
};

const validateWhatsappFlowAction = action => {
  if (!action.flowInboxId)
    return validationResult(false, copy.labels.flowInbox);
  if (!action.flowId) return validationResult(false, copy.labels.flowSelect);
  const flowRecord = whatsappFlowById(action.flowId);
  if (whatsappFlowsLoaded.value) {
    if (
      !flowRecord ||
      Number(flowRecord.inbox_id) !== Number(action.flowInboxId)
    ) {
      return validationResult(false, copy.labels.flowNotFound);
    }
    const sendable =
      flowRecord.status === 'PUBLISHED' ||
      (flowRecord.status === 'DRAFT' && action.flowMode === 'draft');
    if (!sendable) return validationResult(false, copy.labels.flowDraftConfirm);
  }
  const cta = String(action.flowCta || '').trim();
  if (!cta || cta.length > 20)
    return validationResult(false, copy.labels.flowCta);
  const body = String(action.flowBody || '').trim();
  if (!body || body.length > 1024) {
    return validationResult(false, copy.labels.flowBody);
  }
  return validationResult(true);
};

const validateAction = node => {
  const action = normalizeNodeAction(node?.action);
  if (action.type === 'send_message') {
    return validationResult(Boolean(action.message), copy.form.actionMessage);
  }
  if (action.type === 'send_file') {
    return validationResult(Boolean(action.file?.name), copy.form.actionFile);
  }
  if (['add_label', 'remove_label'].includes(action.type)) {
    return validationResult(Boolean(action.label), copy.labels.labelField);
  }
  if (action.type === 'add_private_note') {
    return validationResult(
      Boolean(action.message),
      copy.labels.privateNoteField
    );
  }
  if (action.type === 'assign_team') {
    return validationResult(Boolean(action.teamId), copy.labels.teamField);
  }
  if (action.type === 'set_priority') {
    return validationResult(
      PRIORITY_OPTIONS.some(option => option.value === action.priority),
      copy.labels.priorityField
    );
  }
  if (action.type === 'move_inbox') {
    return validationResult(
      Boolean(action.targetInboxId),
      copy.labels.targetInbox
    );
  }
  if (action.type === 'call_webhook') {
    return validationResult(false, copy.labels.outboundWebhookUnavailable);
  }
  if (action.type === 'http_request') {
    const result = validateHttpAction(action);
    return validationResult(result.isValid, result.message);
  }
  if (
    node?.type === 'decision' &&
    normalizeCondition(action.condition).rules.length
  ) {
    const result = validateCondition(action.condition);
    return validationResult(result.isValid, result.message);
  }
  if (action.type === 'send_whatsapp_flow') {
    return validateWhatsappFlowAction(action);
  }
  if (action.type === 'run_credit_engine') {
    const engine = creditEngines.value.find(
      item => Number(item.id) === Number(action.creditEngineId)
    );
    return validationResult(
      Boolean(action.creditEngineId) &&
        Boolean(engine) &&
        engine.active !== false,
      copy.form.creditEngine
    );
  }
  return validationResult(true);
};

const validateNodeForTest = node =>
  node?.type === 'trigger' ? validateTrigger(node) : validateAction(node);

const pushTestLog = (node, status, validation) => {
  testRun.logs.push({
    id: `${node?.id || 'flow'}-${Date.now()}-${testRun.logs.length}`,
    title: node?.title || flow.name,
    detail: status,
    summary: node ? nodeExecutionSummary(node) : '',
    status: validation?.isValid === false ? 'error' : 'success',
    hint: validation?.isValid === false ? validation.message : '',
  });
};

const runTestFlow = startFromNode => {
  clearTestRun();
  testRun.isModalOpen = true;

  const selectedTrigger =
    selectedNode.value?.type === 'trigger' ? selectedNode.value : null;
  // Button handlers may pass the click event; only a real node can start the test.
  const explicitStart = flow.nodes.find(node => node.id === startFromNode?.id);
  const startNode =
    explicitStart ||
    selectedTrigger ||
    flow.nodes.find(node => node.type === 'trigger');

  if (!startNode) {
    testRun.logs = [
      {
        id: `empty-${Date.now()}`,
        title: copy.labels.testRun,
        detail: copy.labels.noTriggerToTest,
        summary: '',
        status: 'error',
        hint: '',
      },
    ];
    return;
  }

  const path = executionPathFrom(startNode);
  testRun.isRunning = true;

  path.forEach((node, index) => {
    const timer = window.setTimeout(() => {
      if (!testRun.isRunning && index > 0) return;

      testRun.statusByNode = {
        ...testRun.statusByNode,
        [node.id]: 'running',
      };
      const validation = validateNodeForTest(node);
      const nextStatus = validation.isValid ? 'success' : 'error';
      testRun.activeNodeId = node.id;
      testRun.statusByNode = {
        ...testRun.statusByNode,
        [node.id]: nextStatus,
      };
      selectedNodeId.value = node.id;
      selectedEdgeId.value = '';
      pushTestLog(
        node,
        index === 0 ? copy.labels.testStarted : copy.labels.actionSettings,
        validation
      );

      if (index === path.length - 1 || !validation.isValid) {
        testRun.isRunning = false;
        testRun.activeNodeId = '';
        testRun.logs.push({
          id: `done-${Date.now()}`,
          title: validation.isValid
            ? copy.labels.testFinished
            : copy.labels.testError,
          detail: validation.isValid
            ? `${index + 1} passo${index === 0 ? '' : 's'} simulado${
                index === 0 ? '' : 's'
              }`
            : validation.message,
          summary: '',
          status: validation.isValid ? 'success' : 'error',
          hint: '',
        });
      }
    }, index * 550);
    testTimers.push(timer);
  });
};

const nodeCenter = node => ({
  x: node.position.x + NODE_WIDTH / 2,
  y: node.position.y + NODE_HEIGHT / 2,
});

const edgePath = edge => {
  const source = flow.nodes.find(node => node.id === edge.source);
  const target = flow.nodes.find(node => node.id === edge.target);
  if (!source || !target) return '';

  const start = nodeCenter(source);
  const end = nodeCenter(target);
  const offset = Math.max(90, Math.abs(end.x - start.x) / 2);
  const startX = start.x + NODE_WIDTH / 2;
  const endX = end.x - NODE_WIDTH / 2;
  const controlA = startX + offset;
  const controlB = endX - offset;

  return `M ${startX} ${start.y} C ${controlA} ${start.y}, ${controlB} ${end.y}, ${endX} ${end.y}`;
};

const nodeStyle = node => {
  const status = testRun.statusByNode[node.id];
  const baseStyle = {
    left: `${node.position.x}px`,
    top: `${node.position.y}px`,
  };

  if (status === 'running') {
    return {
      ...baseStyle,
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.18)',
      boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.28)',
    };
  }

  if (status === 'success') {
    return {
      ...baseStyle,
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.32)',
    };
  }

  if (status === 'error') {
    return {
      ...baseStyle,
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.32)',
    };
  }

  return baseStyle;
};

const edgeLabelPosition = edge => {
  const source = flow.nodes.find(node => node.id === edge.source);
  const target = flow.nodes.find(node => node.id === edge.target);
  if (!source || !target) return { x: 0, y: 0 };

  const start = nodeCenter(source);
  const end = nodeCenter(target);
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 - 12 };
};

const legacyStagesFromNodes = () => [
  {
    id: 'visual-flow',
    title: flow.name,
    cards: flow.nodes.map(node => ({
      id: node.id,
      title: node.title,
      description: node.description,
      source: node.source,
      leads: node.leads,
      investment: node.investment,
      conversion: node.conversion,
      potential: node.potential,
      color: node.color,
      linkType: node.linkType,
      metadata: {
        type: node.type,
        position: node.position,
        action: node.action,
        trigger: node.trigger,
      },
    })),
  },
];

const flowPayload = () => ({
  name: flow.name,
  active: automationSettings.active,
  stages: legacyStagesFromNodes(),
  settings: {
    builder: 'talkvia-flow-v1',
    nodes: flow.nodes,
    edges: flow.edges,
    viewport: flow.viewport,
    inbox_id: automationSettings.inboxId || null,
  },
});

const applyFlowState = nextFlow => {
  flow.name = nextFlow.name || 'Funil de Vendas';
  flow.nodes = nextFlow.nodes.map(makeNode);
  flow.edges = nextFlow.edges || [];
  automationSettings.inboxId = nextFlow.inboxId || '';
  automationSettings.active = nextFlow.active !== false;
  flow.viewport = {
    x: Number(nextFlow.viewport?.x ?? 24),
    y: Number(nextFlow.viewport?.y ?? 36),
    zoom: Number(nextFlow.viewport?.zoom ?? 0.85),
  };
};

const flowFromLegacyStages = remoteFlow => {
  const stages = Array.isArray(remoteFlow.stages) ? remoteFlow.stages : [];
  const nodes = stages.flatMap((stage, stageIndex) =>
    (stage.cards || []).map((card, cardIndex) =>
      makeNode({
        ...card,
        type: card.metadata?.type || (stageIndex === 0 ? 'trigger' : 'action'),
        position: card.metadata?.position || {
          x: 90 + stageIndex * 340,
          y: 110 + cardIndex * 190,
        },
      })
    )
  );

  const edges = stages
    .slice(0, -1)
    .flatMap((stage, stageIndex) => {
      const nextStage = stages[stageIndex + 1];
      return (stage.cards || []).map((card, cardIndex) => {
        const target =
          nextStage?.cards?.[Math.min(cardIndex, nextStage.cards.length - 1)];
        return target
          ? {
              id: `edge-${card.id}-${target.id}`,
              source: card.id,
              target: target.id,
              label: nextStage.title,
            }
          : null;
      });
    })
    .filter(Boolean);

  return {
    name: remoteFlow.name,
    nodes: nodes.length ? nodes : defaultFlow().nodes,
    edges: edges.length ? edges : defaultFlow().edges,
    viewport: remoteFlow.settings?.viewport || defaultFlow().viewport,
  };
};

const applyRemoteFlow = remoteFlow => {
  currentFunnelId.value = remoteFlow.id;
  const settings = remoteFlow.settings || {};
  if (settings.builder === 'talkvia-flow-v1' || Array.isArray(settings.nodes)) {
    applyFlowState({
      name: remoteFlow.name,
      nodes: Array.isArray(settings.nodes) ? settings.nodes : [],
      edges: Array.isArray(settings.edges) ? settings.edges : [],
      viewport: settings.viewport,
      inboxId: settings.inbox_id || settings.inboxId || '',
      active: remoteFlow.active !== false,
    });
    return;
  }

  applyFlowState({
    ...flowFromLegacyStages(remoteFlow),
    inboxId: settings.inbox_id || settings.inboxId || '',
    active: remoteFlow.active !== false,
  });
};

const syncCurrentFunnelInList = funnel => {
  const index = funnels.value.findIndex(item => item.id === funnel.id);
  if (index >= 0) {
    funnels.value.splice(index, 1, funnel);
  } else {
    funnels.value.unshift(funnel);
  }
};

const createRemoteFlow = async (name = 'Nova automação') => {
  const fresh = defaultFlow();
  const { data } = await TalkviaFunnelsAPI.create({
    name,
    active: true,
    stages: [],
    settings: {
      builder: 'talkvia-flow-v1',
      nodes: fresh.nodes,
      edges: fresh.edges,
      viewport: fresh.viewport,
      inbox_id: automationSettings.inboxId || null,
    },
  });
  syncCurrentFunnelInList(data);
  applyRemoteFlow(data);
};

const createProject = async () => {
  await createRemoteFlow(`Automação ${funnels.value.length + 1}`);
};

const selectProject = funnelId => {
  const funnel = funnels.value.find(
    item => Number(item.id) === Number(funnelId)
  );
  if (funnel) applyRemoteFlow(funnel);
};

const refreshCurrentFunnel = async () => {
  if (!currentFunnelId.value) return;

  try {
    const { data } = await TalkviaFunnelsAPI.show(currentFunnelId.value);
    syncCurrentFunnelInList(data);
    const previousSelectedNodeId = selectedNodeId.value;
    applyRemoteFlow(data);
    selectedNodeId.value = previousSelectedNodeId;
  } catch {
    saveError.value = true;
  }
};

const webhookPayloadFingerprint = node => {
  const trigger = normalizeNodeTrigger(node?.trigger);
  return JSON.stringify({
    payload: trigger.lastPayload || null,
    receivedAt: trigger.lastReceivedAt || '',
  });
};

const waitForWebhookAndTest = async node => {
  if (!node || node.type !== 'trigger') return;

  clearTestRun();
  testRun.isModalOpen = true;
  testRun.isRunning = true;
  testRun.activeNodeId = node.id;
  testRun.statusByNode = {
    ...testRun.statusByNode,
    [node.id]: 'running',
  };
  testRun.logs = [
    {
      id: `webhook-wait-${Date.now()}`,
      title: copy.labels.webhookWaiting,
      detail: copy.labels.webhookWaitingDetail,
      summary: webhookEndpointFor(node),
      status: 'success',
      hint: '',
    },
  ];

  const initialFingerprint = webhookPayloadFingerprint(node);
  webhookWaitStartedAt = Date.now();

  const poll = async () => {
    if (!testRun.isRunning || testRun.activeNodeId !== node.id) return;

    await refreshCurrentFunnel();
    const refreshedNode = flow.nodes.find(item => item.id === node.id);
    const hasNewPayload =
      webhookPayloadFingerprint(refreshedNode) !== initialFingerprint &&
      refreshedNode?.trigger?.lastPayload;

    if (hasNewPayload) {
      testRun.logs.push({
        id: `webhook-received-${Date.now()}`,
        title: copy.labels.webhookReceived,
        detail: refreshedNode.trigger.lastReceivedAt || copy.labels.testStarted,
        summary: triggerSummary(refreshedNode),
        status: 'success',
        hint: '',
      });
      runTestFlow(refreshedNode);
      return;
    }

    if (Date.now() - webhookWaitStartedAt > 60000) {
      testRun.isRunning = false;
      testRun.activeNodeId = '';
      testRun.statusByNode = {
        ...testRun.statusByNode,
        [node.id]: 'error',
      };
      testRun.logs.push({
        id: `webhook-timeout-${Date.now()}`,
        title: copy.labels.testError,
        detail: copy.labels.webhookWaitTimeout,
        summary: '',
        status: 'error',
        hint: '',
      });
      return;
    }

    webhookWaitTimer = window.setTimeout(poll, 2500);
  };

  webhookWaitTimer = window.setTimeout(poll, 2500);
};

const loadCreditEngines = async () => {
  try {
    const { data } = await TalkviaCreditEnginesAPI.get({ active: true });
    creditEngines.value = data.engines || [];
  } catch {
    creditEngines.value = [];
  }
};

const openCreditEngineSettings = () => {
  router.push({
    name: 'talkvia_credit_engines',
    params: { accountId: route.params.accountId },
  });
};

const loadFlow = async () => {
  isLoading.value = true;
  saveError.value = false;

  try {
    const { data } = await TalkviaFunnelsAPI.get();
    funnels.value = data.funnels || [];
    const [firstFunnel] = funnels.value;

    if (firstFunnel) {
      applyRemoteFlow(firstFunnel);
    } else {
      await createRemoteFlow('Automação principal');
    }
  } catch {
    saveError.value = true;
  } finally {
    isLoading.value = false;
    hasLoadedRemoteFlow.value = true;
  }
};

const saveFlow = async () => {
  if (!hasLoadedRemoteFlow.value || !currentFunnelId.value) return;

  isSaving.value = true;
  saveError.value = false;
  hasPendingSave.value = false;

  try {
    const { data } = await TalkviaFunnelsAPI.update(
      currentFunnelId.value,
      flowPayload()
    );
    syncCurrentFunnelInList(data);
  } catch {
    hasPendingSave.value = true;
    saveError.value = true;
  } finally {
    isSaving.value = false;
  }
};

const queueSave = () => {
  hasPendingSave.value = true;
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(saveFlow, 650);
};

const resetFlow = () => {
  clearTestRun();
  applyFlowState(defaultFlow());
  selectedNodeId.value = '';
  selectedEdgeId.value = '';
};

const addNode = (type, options = {}) => {
  const existingOffset = flow.nodes.length * 28;
  const node = makeNode({
    type,
    title:
      type === 'trigger'
        ? 'Cliente iniciou conversa'
        : copy.nodeTypes[type] || 'Novo nó',
    description: '',
    color: nodeColorForType(type),
    position: {
      x: 180 + existingOffset,
      y: 140 + existingOffset,
    },
    ...options,
  });
  flow.nodes.push(node);
  selectedNodeId.value = node.id;
  selectedEdgeId.value = '';
};

const addTriggerNode = () => addNode('trigger');

const addWebhookTriggerNode = () =>
  addNode('trigger', {
    title: copy.triggerTypes.webhook_received,
    description: 'Recebe JSON externo e libera campos para o próximo passo.',
    color: 'emerald',
    trigger: { type: 'webhook_received', webhookMethod: 'POST' },
  });

const useWebhookOnSelectedTrigger = () => {
  if (!selectedNode.value || selectedNode.value.type !== 'trigger') return;

  selectedNode.value.title = copy.triggerTypes.webhook_received;
  selectedNode.value.color = 'emerald';
  selectedNode.value.trigger = {
    ...normalizeNodeTrigger(selectedNode.value.trigger),
    type: 'webhook_received',
    webhookMethod: selectedNode.value.trigger?.webhookMethod || 'POST',
  };
};

const webhookDefaultsFor = node => {
  const triggerNode =
    node?.type === 'trigger' &&
    normalizeNodeTrigger(node.trigger).type === 'webhook_received'
      ? node
      : upstreamWebhookTriggerFor(node);
  const trigger = normalizeNodeTrigger(triggerNode?.trigger);

  return {
    contactPath: trigger.contactPath || 'cliente.nome',
    phonePath: trigger.phonePath || 'cliente.telefone',
    messagePath: trigger.messagePath || 'mensagem',
  };
};

// Sugere a caixa do funil quando ela é WhatsApp Cloud, ou a única caixa Cloud.
const defaultWhatsappFlowInboxId = () => {
  const funnelInboxId = Number(automationSettings.inboxId);
  const cloudInboxIds = cloudInboxOptions.value.map(inbox => inbox.value);
  if (cloudInboxIds.includes(funnelInboxId)) return funnelInboxId;
  return cloudInboxIds.length === 1 ? cloudInboxIds[0] : '';
};

const defaultActionForType = (actionType, sourceNode) => {
  const webhookDefaults = webhookDefaultsFor(sourceNode);
  const action = {
    ...emptyNodeAction(),
    type: actionType || 'none',
    ...webhookDefaults,
  };

  if (action.type === 'send_message' && webhookDefaults.messagePath) {
    action.message = `{{${webhookDefaults.messagePath}}}`;
  }

  if (action.type === 'send_whatsapp_flow') {
    action.flowInboxId = defaultWhatsappFlowInboxId();
    ensureWhatsappFlowsLoaded();
  }

  if (action.type === 'http_request')
    Object.assign(action, defaultHttpAction());

  return action;
};

const openNodePicker = node => {
  if (!node) return;
  nodePicker.visible = true;
  nodePicker.sourceNodeId = node.id;
  nodePicker.search = '';
};

const closeNodePicker = () => {
  nodePicker.visible = false;
  nodePicker.sourceNodeId = '';
  nodePicker.search = '';
};

const syncActionDefaults = node => {
  if (!node || node.type === 'trigger') return;
  const action = normalizeNodeAction(node.action);
  const webhookDefaults = webhookDefaultsFor(node);

  node.action = {
    ...action,
    contactPath: action.contactPath || webhookDefaults.contactPath,
    phonePath: action.phonePath || webhookDefaults.phonePath,
    messagePath: action.messagePath || webhookDefaults.messagePath,
  };

  if (
    node.action.type === 'send_message' &&
    !node.action.message &&
    node.action.messagePath
  ) {
    node.action.message = `{{${node.action.messagePath}}}`;
  }

  if (node.action.type === 'send_whatsapp_flow' && !node.action.flowInboxId) {
    node.action.flowInboxId = defaultWhatsappFlowInboxId();
  }
};

const selectActionType = actionType => {
  const node = selectedNode.value;
  if (!node || !actionType || node.action?.type === actionType) return;
  node.action = { ...normalizeNodeAction(node.action), type: actionType };
  if (actionType === 'http_request' && !node.action.httpUrl) {
    node.action = { ...node.action, ...defaultHttpAction() };
  }
  syncActionDefaults(node);
  if (actionType === 'send_whatsapp_flow') ensureWhatsappFlowsLoaded();
};

const insertActionJsonVariable = field => {
  if (!selectedNode.value || selectedNode.value.type === 'trigger' || !field)
    return;
  const action = normalizeNodeAction(selectedNode.value.action);
  selectedNode.value.action = {
    ...action,
    message: `${action.message || ''}{{${field}}}`,
  };
};

const addConnectedNodeAfter = (node, type = 'action', options = {}) => {
  if (!node) return;

  const actionType = options.actionType || 'none';
  const nextNode = makeNode({
    type,
    title:
      options.title ||
      (type === 'action' ? copy.nodeTypes.action : copy.nodeTypes[type]),
    description: options.description || '',
    color: options.color || nodeColorForType(type),
    action:
      type === 'action'
        ? defaultActionForType(actionType, node)
        : emptyNodeAction(),
    position: {
      x: node.position.x + NODE_WIDTH + 140,
      y: node.position.y,
    },
  });

  flow.nodes.push(nextNode);
  flow.edges.push({
    id: `edge-${node.id}-${nextNode.id}-${Date.now()}`,
    source: node.id,
    target: nextNode.id,
    label: branchLabelsForNode(node).length
      ? suggestBranchLabel(node, flow.edges)
      : (actionType !== 'none' && copy.actionTypes[actionType]) || '',
  });
  selectedNodeId.value = nextNode.id;
  selectedEdgeId.value = '';
  closeNodePicker();
  isNodeInspectorOpen.value = true;
};

const addNodeFromPicker = option => {
  const sourceNode = flow.nodes.find(
    node => node.id === nodePicker.sourceNodeId
  );
  addConnectedNodeAfter(sourceNode, option.type, option);
};

const addNodeFromShortcut = shortcut => {
  if (shortcut.type === 'trigger') {
    addNode('trigger', {
      title: shortcut.title,
      color: shortcut.color,
      trigger: {
        type: shortcut.triggerType,
        webhookMethod: 'POST',
      },
    });
    return;
  }
  addNode(shortcut.type, {
    title: shortcut.title,
    color: shortcut.color,
    action:
      shortcut.type === 'action'
        ? defaultActionForType(shortcut.actionType, null)
        : emptyNodeAction(),
  });
  isNodeInspectorOpen.value = true;
};

const closeContextMenu = () => {
  contextMenu.visible = false;
  contextMenu.nodeId = '';
};

const duplicateNode = node => {
  if (!node) return;
  const copyNode = makeNode({
    ...node,
    id: undefined,
    title: `${node.title} cópia`,
    position: {
      x: node.position.x + 36,
      y: node.position.y + 36,
    },
  });
  flow.nodes.push(copyNode);
  selectedNodeId.value = copyNode.id;
  selectedEdgeId.value = '';
};

const removeNode = nodeId => {
  flow.nodes = flow.nodes.filter(node => node.id !== nodeId);
  flow.edges = flow.edges.filter(
    edge => edge.source !== nodeId && edge.target !== nodeId
  );
  selectedNodeId.value = '';
  closeContextMenu();
};

const uploadActionFile = async file => {
  if (!file || !selectedNode.value || isUploadingFile.value) return;
  const node = selectedNode.value;

  isUploadingFile.value = true;
  uploadError.value = false;
  try {
    const uploaded = await uploadFile(file, route.params.accountId);
    node.action = {
      ...normalizeNodeAction(node.action),
      type: 'send_file',
      file: {
        name: file.name,
        size: file.size,
        type: file.type,
        fileUrl: uploaded.fileUrl,
        blobId: uploaded.blobId,
        blobKey: uploaded.blobKey,
      },
    };
  } catch {
    uploadError.value = true;
    useAlert(copy.labels.uploadError);
  } finally {
    isUploadingFile.value = false;
  }
};

const removeActionFile = () => {
  if (!selectedNode.value || isUploadingFile.value) return;
  patchSelectedAction({ file: null });
};

const removeEdge = edgeId => {
  flow.edges = flow.edges.filter(edge => edge.id !== edgeId);
  selectedEdgeId.value = '';
};

const removeSelected = () => {
  if (selectedNodeId.value) {
    removeNode(selectedNodeId.value);
    return;
  }

  if (selectedEdgeId.value) removeEdge(selectedEdgeId.value);
};

const openEdgeEditor = edgeId => {
  if (!flow.edges.some(edge => edge.id === edgeId)) return;
  edgeEditor.visible = true;
  edgeEditor.edgeId = edgeId;
};

const closeEdgeEditor = () => {
  edgeEditor.visible = false;
  edgeEditor.edgeId = '';
};

const createEdge = (sourceId, targetId) => {
  if (!sourceId || !targetId || sourceId === targetId) return;

  if (canConnectNodes(flow.edges, sourceId, targetId)) {
    const sourceNode = flow.nodes.find(node => node.id === sourceId);
    const edge = {
      id: `edge-${sourceId}-${targetId}-${Date.now()}`,
      source: sourceId,
      target: targetId,
      label: suggestBranchLabel(sourceNode, flow.edges),
    };
    flow.edges.push(edge);
    // Condição, HTTP e demais nós com resultado pedem o caminho da ligação.
    if (branchLabelsForNode(sourceNode).length) openEdgeEditor(edge.id);
  }

  isConnecting.value = false;
  connectionSourceId.value = '';
};

const editedEdge = computed(() =>
  flow.edges.find(edge => edge.id === edgeEditor.edgeId)
);
const editedEdgeSource = computed(() =>
  flow.nodes.find(node => node.id === editedEdge.value?.source)
);
const editedEdgeBranches = computed(() =>
  branchLabelsForNode(editedEdgeSource.value)
);

const setEditedEdgeLabel = label => {
  if (editedEdge.value) editedEdge.value.label = label;
};

const removeEditedEdge = () => {
  if (!editedEdge.value) return;
  removeEdge(editedEdge.value.id);
  closeEdgeEditor();
};

const startConnection = nodeId => {
  closeContextMenu();
  connectionSourceId.value = nodeId;
  isConnecting.value = true;
  selectedNodeId.value = nodeId;
  selectedEdgeId.value = '';
};

const finishConnection = nodeId => {
  if (!connectionSourceId.value) return;
  createEdge(connectionSourceId.value, nodeId);
};

const nodeSnapshot = nodeId =>
  JSON.stringify(flow.nodes.find(node => node.id === nodeId) || null);

const openNodeInspector = nodeId => {
  if (!nodeId) return;
  closeContextMenu();
  selectedNodeId.value = nodeId;
  selectedEdgeId.value = '';
  isNodeInspectorOpen.value = true;
  breadcrumbMenu.value = '';
  inspectorSnapshot = nodeSnapshot(nodeId);
};

const closeNodeInspector = () => {
  isNodeInspectorOpen.value = false;
  isConfirmingNodeRemoval.value = false;
  breadcrumbMenu.value = '';
};

// Navegação no modal "Editar nó": vizinhos pelas ligações e ordem do fluxo.
const selectedIncomingNodes = computed(() =>
  selectedNode.value
    ? incomingNodes(flow.nodes, flow.edges, selectedNode.value.id)
    : []
);
const selectedOutgoingNodes = computed(() =>
  selectedNode.value
    ? outgoingNodes(flow.nodes, flow.edges, selectedNode.value.id)
    : []
);
const previousFlowNode = computed(() =>
  selectedNode.value
    ? adjacentFlowNode(flow.nodes, flow.edges, selectedNode.value.id, -1)
    : null
);
const nextFlowNode = computed(() =>
  selectedNode.value
    ? adjacentFlowNode(flow.nodes, flow.edges, selectedNode.value.id, 1)
    : null
);
const nodeJumpOptions = computed(() =>
  orderedFlowNodes(flow.nodes, flow.edges).map((node, index) => ({
    value: node.id,
    label: `${index + 1}. ${node.title || nodeTypeMeta(node.type).label} · ${nodeTypeMeta(node.type).label}`,
  }))
);

const hasUnsavedNodeChanges = () =>
  Boolean(selectedNode.value) &&
  nodeSnapshot(selectedNode.value.id) !== inspectorSnapshot &&
  (hasPendingSave.value || isSaving.value || saveError.value);

const goToNode = nodeId => {
  if (!nodeId || !flow.nodes.some(node => node.id === nodeId)) return;
  breadcrumbMenu.value = '';
  if (nodeId === selectedNodeId.value) return;
  if (hasUnsavedNodeChanges()) {
    pendingNavigationTarget.value = nodeId;
    pendingNavigationDialog.value?.open?.();
    return;
  }
  openNodeInspector(nodeId);
};

const cancelPendingNavigation = () => {
  pendingNavigationTarget.value = '';
};

const confirmPendingNavigation = async () => {
  const target = pendingNavigationTarget.value;
  if (!target) return;
  isSavingBeforeNavigation.value = true;
  window.clearTimeout(saveTimer);
  await saveFlow();
  isSavingBeforeNavigation.value = false;
  if (saveError.value) {
    useAlert(copy.labels.pendingSaveError);
    return;
  }
  pendingNavigationDialog.value?.close?.();
  pendingNavigationTarget.value = '';
  openNodeInspector(target);
};

const openBreadcrumbTarget = direction => {
  const candidates =
    direction === 'in'
      ? selectedIncomingNodes.value
      : selectedOutgoingNodes.value;
  if (!candidates.length) return;
  if (candidates.length === 1) {
    goToNode(candidates[0].id);
    return;
  }
  breadcrumbMenu.value = breadcrumbMenu.value === direction ? '' : direction;
};

const confirmSelectedNodeRemoval = () => {
  if (!selectedNode.value) return;
  const nodeId = selectedNode.value.id;
  closeNodeInspector();
  removeNode(nodeId);
};

watch(selectedNodeId, () => {
  isConfirmingNodeRemoval.value = false;
});

// Referência para detectar edição pendente ao navegar entre nós no modal.
watch([selectedNodeId, isNodeInspectorOpen], ([nodeId, isOpen]) => {
  if (isOpen) inspectorSnapshot = nodeSnapshot(nodeId);
});

watch(
  () => [isNodeInspectorOpen.value, selectedNode.value?.action?.type],
  ([isOpen, actionType]) => {
    if (isOpen && actionType === 'send_whatsapp_flow') {
      ensureWhatsappFlowsLoaded();
    }
  }
);

const selectNode = (nodeId, options = {}) => {
  closeContextMenu();
  if (isConnecting.value) {
    if (!connectionSourceId.value) {
      startConnection(nodeId);
      return;
    }

    createEdge(connectionSourceId.value, nodeId);
    return;
  }

  selectedNodeId.value = nodeId;
  selectedEdgeId.value = '';

  if (options.openInspector) {
    isNodeInspectorOpen.value = true;
  }
};

const selectEdge = edgeId => {
  closeContextMenu();
  selectedEdgeId.value = edgeId;
  selectedNodeId.value = '';
};

const editEdge = edgeId => {
  selectEdge(edgeId);
  openEdgeEditor(edgeId);
};

const toggleConnectMode = () => {
  isConnecting.value = !isConnecting.value;
  connectionSourceId.value = '';
};

const connectionStatusLabel = computed(() =>
  connectionSourceId.value ? copy.labels.connectActive : copy.labels.connectIdle
);

const zoomBy = amount => {
  flow.viewport.zoom = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, flow.viewport.zoom + amount)
  );
};

const fitView = () => {
  flow.viewport.x = 24;
  flow.viewport.y = 36;
  flow.viewport.zoom = 0.85;
};

const openNodeContextMenu = (event, node) => {
  event.preventDefault();
  event.stopPropagation();
  selectedNodeId.value = node.id;
  selectedEdgeId.value = '';
  contextMenu.visible = true;
  contextMenu.nodeId = node.id;
  contextMenu.x = event.clientX;
  contextMenu.y = event.clientY;
};

const runContextAction = action => {
  const node = contextMenuNode.value;
  if (!node) {
    closeContextMenu();
    return;
  }

  if (action === 'edit') openNodeInspector(node.id);
  if (action === 'duplicate') duplicateNode(node);
  if (action === 'connect') startConnection(node.id);
  if (action === 'open') openLinkedResource(node);
  if (action === 'remove') removeNode(node.id);
  closeContextMenu();
};

const NODE_SIZE = { width: NODE_WIDTH, height: NODE_HEIGHT };

const resetLinkDrag = () => {
  linkDrag.active = false;
  linkDrag.sourceId = '';
  linkDrag.moved = false;
  linkDrag.targetId = '';
};

const isTypingTarget = target => {
  const tagName = target?.tagName?.toLowerCase();
  return (
    target?.isContentEditable ||
    ['input', 'textarea', 'select'].includes(tagName)
  );
};

const onKeyDown = event => {
  if (isTypingTarget(event.target)) return;

  if (
    event.altKey &&
    isNodeInspectorOpen.value &&
    ['ArrowLeft', 'ArrowRight'].includes(event.key)
  ) {
    event.preventDefault();
    const target =
      event.key === 'ArrowLeft' ? previousFlowNode.value : nextFlowNode.value;
    if (target) goToNode(target.id);
    return;
  }

  if (event.key === 'Escape' && edgeEditor.visible) {
    closeEdgeEditor();
    return;
  }

  if (event.key === 'Escape' && linkDrag.active) {
    resetLinkDrag();
    return;
  }

  if (event.key === 'Escape') {
    closeNodeInspector();
    closeNodePicker();
    closeContextMenu();
    isConnecting.value = false;
    connectionSourceId.value = '';
    return;
  }

  if (event.key === 'Delete' || event.key === 'Backspace') {
    if (!selectedNodeId.value && !selectedEdgeId.value) return;
    event.preventDefault();
    removeSelected();
  }
};

const canvasPointFromEvent = event =>
  clientToCanvasPoint(
    { x: event.clientX, y: event.clientY },
    canvasEl.value?.getBoundingClientRect(),
    flow.viewport
  );

const startLinkDrag = (event, node) => {
  if (event.button !== undefined && event.button > 0) return;
  closeContextMenu();
  linkDrag.active = true;
  linkDrag.sourceId = node.id;
  linkDrag.originClient = { x: event.clientX, y: event.clientY };
  linkDrag.pointer = canvasPointFromEvent(event);
  linkDrag.moved = false;
  linkDrag.targetId = '';
};

const updateLinkDrag = event => {
  linkDrag.pointer = canvasPointFromEvent(event);
  if (
    !linkDrag.moved &&
    hasMovedBeyond(linkDrag.originClient, {
      x: event.clientX,
      y: event.clientY,
    })
  ) {
    linkDrag.moved = true;
  }
  if (!linkDrag.moved) return;
  const target = findNodeAtPoint(flow.nodes, linkDrag.pointer, NODE_SIZE);
  linkDrag.targetId =
    target && canConnectNodes(flow.edges, linkDrag.sourceId, target.id)
      ? target.id
      : '';
};

const finishLinkDrag = () => {
  if (!linkDrag.active) return;
  if (linkDrag.moved) {
    // O clique que o navegador dispara após o arraste não abre o seletor.
    suppressHandleClick = true;
    window.setTimeout(() => {
      suppressHandleClick = false;
    }, 0);
    if (linkDrag.targetId) {
      createEdge(linkDrag.sourceId, linkDrag.targetId);
      selectedNodeId.value = linkDrag.targetId;
      selectedEdgeId.value = '';
    }
  }
  resetLinkDrag();
};

const onOutputHandleClick = node => {
  if (suppressHandleClick) {
    suppressHandleClick = false;
    return;
  }
  openNodePicker(node);
};

const linkDragPath = computed(() => {
  if (!linkDrag.active || !linkDrag.moved) return '';
  const source = flow.nodes.find(node => node.id === linkDrag.sourceId);
  if (!source) return '';
  const target = flow.nodes.find(node => node.id === linkDrag.targetId);
  const end = target ? inputAnchor(target, NODE_SIZE) : linkDrag.pointer;
  return connectionCurvePath(outputAnchor(source, NODE_SIZE), end);
});

const startNodeDrag = (event, node) => {
  if (isConnecting.value || event.target.closest('[data-node-action]')) return;
  const point = canvasPointFromEvent(event);
  dragState.value = {
    nodeId: node.id,
    offsetX: point.x - node.position.x,
    offsetY: point.y - node.position.y,
  };
  selectNode(node.id, { openInspector: false });
};

const startPan = event => {
  closeContextMenu();
  if (event.target.closest('[data-flow-node]')) return;
  panState.value = {
    startX: event.clientX,
    startY: event.clientY,
    originX: flow.viewport.x,
    originY: flow.viewport.y,
  };
  selectedNodeId.value = '';
  selectedEdgeId.value = '';
};

const onPointerMove = event => {
  if (linkDrag.active) {
    updateLinkDrag(event);
    return;
  }

  if (dragState.value) {
    const point = canvasPointFromEvent(event);
    const node = flow.nodes.find(item => item.id === dragState.value.nodeId);
    if (node) {
      node.position.x = Math.round(point.x - dragState.value.offsetX);
      node.position.y = Math.round(point.y - dragState.value.offsetY);
    }
    return;
  }

  if (panState.value) {
    flow.viewport.x =
      panState.value.originX + event.clientX - panState.value.startX;
    flow.viewport.y =
      panState.value.originY + event.clientY - panState.value.startY;
  }
};

const stopPointerInteraction = () => {
  finishLinkDrag();
  dragState.value = null;
  panState.value = null;
};

const cancelPointerInteraction = () => {
  resetLinkDrag();
  dragState.value = null;
  panState.value = null;
};

const onCanvasWheel = event => {
  event.preventDefault();
  const nextZoom = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, flow.viewport.zoom + (event.deltaY > 0 ? -0.08 : 0.08))
  );
  flow.viewport.zoom = Number(nextZoom.toFixed(2));
};

watch(hasCorban, enabled => {
  if (enabled && !creditEngines.value.length) loadCreditEngines();
});

watch(currentFunnelId, () => {
  flowMetrics.value = null;
  loadFlowMetrics();
});

watch(flow, queueSave, { deep: true });
watch(automationSettings, queueSave, { deep: true });

onMounted(async () => {
  await Promise.all([
    store.dispatch('inboxes/getFromNetwork'),
    store.dispatch('labels/get').catch(() => {}),
    store.dispatch('teams/get').catch(() => {}),
    store.dispatch('attributes/get')?.catch?.(() => {}),
    loadFlow(),
    hasCorban.value ? loadCreditEngines() : Promise.resolve(),
  ]);
  if (
    flow.nodes.some(
      node => normalizeNodeAction(node.action).type === 'send_whatsapp_flow'
    )
  ) {
    ensureWhatsappFlowsLoaded();
  }
  await nextTick();
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopPointerInteraction);
  window.addEventListener('pointercancel', cancelPointerInteraction);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('click', closeContextMenu);
});

onBeforeUnmount(() => {
  window.clearTimeout(saveTimer);
  clearTestRun();
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopPointerInteraction);
  window.removeEventListener('pointercancel', cancelPointerInteraction);
  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('click', closeContextMenu);
});
</script>

<template>
  <main
    class="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-n-background text-n-slate-12"
  >
    <section class="flex min-h-0 flex-1 flex-col">
      <header
        class="flex shrink-0 flex-col gap-4 border-b border-n-weak bg-n-solid-1 px-4 py-4 lg:flex-row lg:items-center lg:justify-between lg:px-6"
      >
        <div class="min-w-0">
          <div
            class="mb-2 flex items-center gap-2 text-xs font-medium uppercase text-n-slate-10"
          >
            <span class="i-lucide-workflow size-3.5" />
            {{ copy.eyebrow }}
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <input
              v-model="flow.name"
              class="min-w-64 rounded-lg border border-transparent bg-transparent px-0 text-xl font-semibold outline-none focus:border-n-brand focus:bg-n-alpha-2 focus:px-3 md:text-2xl"
              :aria-label="copy.form.name"
            />
            <span
              class="inline-flex h-8 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-xs font-medium"
              :class="saveStatusClass"
            >
              <span class="i-lucide-cloud size-4" />
              {{ saveStatusLabel }}
            </span>
          </div>
          <div class="mt-3 flex flex-wrap items-center gap-2">
            <div class="flow-header-field min-w-56">
              <span class="flow-header-field-label">
                {{ copy.form.project }}
              </span>
              <ComboBox
                :model-value="idValue(currentFunnelId)"
                :options="projectOptions"
                :placeholder="copy.labels.selectPlaceholder"
                :search-placeholder="copy.labels.searchPlaceholder"
                :empty-state="copy.labels.noResults"
                teleport-to-body
                @update:model-value="selectProject"
              />
            </div>
            <div class="flow-header-field min-w-40">
              <span class="flow-header-field-label">
                {{ copy.form.status }}
              </span>
              <ComboBox
                v-model="automationStatusValue"
                :options="automationStatusOptions"
                :display-label="
                  optionLabel(automationStatusOptions, automationStatusValue)
                "
                :placeholder="copy.labels.selectPlaceholder"
                :search-placeholder="copy.labels.searchPlaceholder"
                :empty-state="copy.labels.noResults"
                teleport-to-body
              />
            </div>
            <div class="flow-header-field min-w-56">
              <span class="flow-header-field-label">
                {{ copy.form.inbox }}
              </span>
              <ComboBox
                :model-value="idValue(automationSettings.inboxId)"
                :options="inboxOptionsWithAll"
                :placeholder="copy.labels.noInbox"
                :search-placeholder="copy.labels.inboxSearch"
                :empty-state="copy.labels.noResults"
                teleport-to-body
                @update:model-value="automationSettings.inboxId = $event"
              />
            </div>
          </div>
          <p class="mt-2 max-w-3xl text-sm text-n-slate-11">
            {{ copy.subtitle }}
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
            @click="createProject"
          >
            <span class="i-lucide-folder-plus size-4" />
            {{ copy.actions.newProject }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
            @click="resetFlow"
          >
            <span class="i-lucide-refresh-cw size-4" />
            {{ copy.actions.reset }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
            @click="fitView"
          >
            <span class="i-lucide-scan size-4" />
            {{ copy.actions.fit }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
            @click="runTestFlow"
          >
            <span class="i-lucide-play-circle size-4" />
            {{ copy.actions.testFlow }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium"
            :class="
              isConnecting
                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                : 'border border-n-weak hover:bg-n-alpha-2'
            "
            @click="toggleConnectMode"
          >
            <span class="i-lucide-git-compare-arrows size-4" />
            {{
              isConnecting ? copy.actions.cancelConnect : copy.actions.connect
            }}
          </button>
        </div>
      </header>

      <div
        class="grid min-h-0 flex-1 grid-cols-1"
        :class="
          isFlowPanelCollapsed
            ? 'lg:grid-cols-[1fr_56px]'
            : 'lg:grid-cols-[1fr_340px]'
        "
      >
        <section class="relative min-w-0 overflow-hidden bg-n-background">
          <div class="absolute left-4 top-4 z-20 grid gap-2">
            <div
              class="flex overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-sm"
            >
              <button
                type="button"
                class="grid size-9 place-content-center hover:bg-n-alpha-2"
                @click="zoomBy(0.1)"
              >
                <span class="i-lucide-plus size-4" />
              </button>
              <span
                class="grid h-9 min-w-14 place-content-center border-x border-n-weak text-xs font-medium"
              >
                {{ zoomLabel }}
              </span>
              <button
                type="button"
                class="grid size-9 place-content-center hover:bg-n-alpha-2"
                @click="zoomBy(-0.1)"
              >
                <span class="i-lucide-minus size-4" />
              </button>
            </div>
            <div
              class="flex flex-wrap items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 shadow-sm"
              data-testid="flow-counters-toolbar"
            >
              <label
                class="flex cursor-pointer items-center gap-2 text-xs font-medium text-n-slate-12"
              >
                <Switch
                  :model-value="showNodeCounters"
                  data-testid="flow-counters-toggle"
                  @update:model-value="toggleNodeCounters"
                />
                {{ copy.metrics.showCounters }}
              </label>
              <div class="w-40">
                <ComboBox
                  :model-value="metricsPeriod"
                  :options="METRIC_PERIODS"
                  :display-label="metricsPeriodLabel"
                  :placeholder="copy.metrics.period"
                  :search-placeholder="copy.labels.searchPlaceholder"
                  :empty-state="copy.labels.noResults"
                  :aria-label="copy.metrics.period"
                  teleport-to-body
                  @update:model-value="selectMetricsPeriod"
                />
              </div>
              <span
                v-if="isLoadingMetrics"
                class="i-hugeicons-loading-03 size-4 text-n-slate-10 motion-safe:animate-spin"
                aria-hidden="true"
              />
            </div>
          </div>

          <div
            v-if="isConnecting"
            class="absolute left-4 top-28 z-20 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 shadow-sm"
          >
            {{ connectionStatusLabel }}
          </div>

          <div class="absolute bottom-4 left-4 z-20 grid gap-2 sm:grid-cols-4">
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.nodes }}
              </p>
              <p class="text-lg font-semibold">{{ nodeCount }}</p>
            </article>
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.edges }}
              </p>
              <p class="text-lg font-semibold">{{ edgeCount }}</p>
            </article>
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
              :title="`${copy.metrics.leadsHelp} ${metricsPeriodLabel}`"
              data-testid="flow-metric-leads"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.leads }}
              </p>
              <p class="text-lg font-semibold">
                {{ totalLeadsLabel }}
              </p>
            </article>
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
              :title="`${copy.metrics.conversionHelp} ${metricsPeriodLabel}`"
              data-testid="flow-metric-conversion"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.conversion }}
              </p>
              <p class="text-lg font-semibold">
                {{ averageConversionLabel }}
              </p>
            </article>
          </div>

          <div
            ref="canvasEl"
            :class="{ '!cursor-crosshair': linkDrag.active && linkDrag.moved }"
            class="h-full w-full cursor-grab overflow-hidden bg-[radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.28)_1px,transparent_0)] [background-size:24px_24px] active:cursor-grabbing"
            @pointerdown="startPan"
            @wheel="onCanvasWheel"
          >
            <div
              class="relative h-[1800px] w-[2200px] origin-top-left"
              :style="canvasStyle"
            >
              <svg
                class="pointer-events-none absolute inset-0 h-full w-full"
                aria-hidden="true"
              >
                <defs>
                  <marker
                    id="flow-arrow-dot"
                    markerHeight="6"
                    markerWidth="6"
                    orient="auto"
                    refX="3"
                    refY="3"
                  >
                    <circle cx="3" cy="3" r="1.8" class="fill-n-brand" />
                  </marker>
                </defs>
                <path
                  v-for="edge in flow.edges"
                  :key="edge.id"
                  :d="edgePath(edge)"
                  fill="none"
                  marker-end="url(#flow-arrow-dot)"
                  stroke="currentColor"
                  stroke-dasharray="1 10"
                  stroke-linecap="round"
                  stroke-width="3"
                  class="pointer-events-auto cursor-pointer text-n-slate-8 hover:text-n-brand"
                  :class="selectedEdgeId === edge.id ? 'text-n-brand' : ''"
                  @click.stop="selectEdge(edge.id)"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    dur="1s"
                    from="11"
                    repeatCount="indefinite"
                    to="0"
                  />
                </path>
                <path
                  v-if="linkDragPath"
                  :d="linkDragPath"
                  fill="none"
                  stroke="currentColor"
                  stroke-dasharray="6 6"
                  stroke-linecap="round"
                  stroke-width="3"
                  class="text-n-brand"
                  data-testid="flow-link-drag-path"
                />
              </svg>

              <button
                v-for="edge in flow.edges"
                :key="`${edge.id}-label`"
                type="button"
                class="absolute z-10 max-w-40 -translate-x-1/2 rounded-full border border-n-weak bg-n-solid-1 px-2 py-1 text-xs font-medium text-n-slate-10 shadow-sm hover:text-n-slate-12"
                :style="{
                  left: `${edgeLabelPosition(edge).x}px`,
                  top: `${edgeLabelPosition(edge).y}px`,
                }"
                :class="selectedEdgeId === edge.id ? '!border-n-brand' : ''"
                data-testid="flow-edge-label"
                @click.stop="editEdge(edge.id)"
              >
                {{ edge.label || copy.labels.edgeDefault }}
              </button>

              <article
                v-for="node in flow.nodes"
                :key="node.id"
                data-flow-node
                class="absolute z-20 flex h-[176px] w-[320px] cursor-grab flex-col rounded-lg border bg-n-solid-1 p-4 shadow-sm transition hover:shadow-md active:cursor-grabbing"
                :class="[
                  selectedNodeId === node.id
                    ? 'border-n-brand ring-2 ring-n-brand/20'
                    : 'border-n-weak',
                  connectionSourceId === node.id ? 'ring-2 ring-amber-300' : '',
                  linkDrag.active && linkDrag.sourceId === node.id
                    ? 'ring-2 ring-n-brand/40'
                    : '',
                  linkDrag.targetId === node.id
                    ? 'flow-link-target border-n-brand ring-4 ring-n-brand/30'
                    : '',
                  testRun.activeNodeId === node.id
                    ? 'ring-2 ring-emerald-400 shadow-emerald-500/20'
                    : '',
                  testRun.statusByNode[node.id] === 'running'
                    ? 'border-amber-300 bg-amber-50/80 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100'
                    : '',
                  testRun.statusByNode[node.id] === 'success'
                    ? 'border-emerald-400 bg-emerald-50/80 text-emerald-950 dark:bg-emerald-950/20 dark:text-emerald-100'
                    : '',
                  testRun.statusByNode[node.id] === 'error'
                    ? 'border-ruby-400 bg-ruby-50/80 text-ruby-950 dark:bg-ruby-950/20 dark:text-ruby-100'
                    : '',
                ]"
                :style="nodeStyle(node)"
                @pointerdown.stop="startNodeDrag($event, node)"
                @click.stop="selectNode(node.id)"
                @dblclick.stop="openNodeInspector(node.id)"
                @contextmenu="openNodeContextMenu($event, node)"
              >
                <button
                  type="button"
                  data-node-action
                  class="absolute -left-3 top-1/2 grid size-6 -translate-y-1/2 place-content-center rounded-full border bg-n-solid-1 shadow-sm motion-safe:transition hover:border-n-brand hover:text-n-brand disabled:opacity-30"
                  :class="
                    linkDrag.targetId === node.id
                      ? 'scale-125 border-n-brand bg-n-brand text-white'
                      : 'border-n-weak text-n-slate-9'
                  "
                  :title="copy.labels.connectorEndTitle"
                  :aria-label="copy.labels.connectorEndTitle"
                  :disabled="
                    !connectionSourceId || connectionSourceId === node.id
                  "
                  @pointerdown.stop
                  @click.stop="finishConnection(node.id)"
                >
                  <span class="i-lucide-dot size-4" />
                </button>
                <button
                  type="button"
                  data-node-action
                  data-node-output
                  class="absolute -right-3 top-1/2 grid size-6 -translate-y-1/2 cursor-crosshair touch-none place-content-center rounded-full border bg-n-solid-1 shadow-sm motion-safe:transition hover:scale-110 hover:border-n-brand hover:text-n-brand"
                  :class="
                    linkDrag.active && linkDrag.sourceId === node.id
                      ? 'border-n-brand text-n-brand'
                      : 'border-n-weak text-n-slate-9'
                  "
                  :title="copy.labels.dragConnectHint"
                  :aria-label="copy.labels.connectorStartTitle"
                  @pointerdown.stop="startLinkDrag($event, node)"
                  @click.stop="onOutputHandleClick(node)"
                >
                  <span class="i-lucide-plus size-3.5" />
                </button>
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div
                      class="mb-2 inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium"
                      :class="nodeToneClass(node.color)"
                    >
                      <span
                        class="size-3.5"
                        :class="[nodeTypeMeta(node.type).icon]"
                      />
                      <span class="truncate">{{
                        nodeTypeMeta(node.type).label
                      }}</span>
                    </div>
                    <h2 class="line-clamp-2 text-sm font-semibold leading-5">
                      {{ node.title }}
                    </h2>
                    <p
                      v-if="node.type === 'trigger'"
                      class="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"
                    >
                      <span class="i-lucide-play-circle size-3" />
                      {{ copy.labels.flowStart }}
                    </p>
                    <p
                      v-if="testRun.statusByNode[node.id]"
                      class="mt-1 inline-flex items-center gap-1 text-[11px] font-medium"
                      :class="
                        testRun.statusByNode[node.id] === 'running'
                          ? 'text-amber-700 dark:text-amber-300'
                          : testRun.statusByNode[node.id] === 'success'
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-ruby-700 dark:text-ruby-300'
                      "
                    >
                      <span
                        class="size-3"
                        :class="
                          testRun.statusByNode[node.id] === 'running'
                            ? 'i-lucide-loader-circle animate-spin'
                            : testRun.statusByNode[node.id] === 'success'
                              ? 'i-lucide-check-circle-2'
                              : 'i-lucide-x-circle'
                        "
                      />
                      {{
                        testRun.statusByNode[node.id] === 'running'
                          ? copy.labels.testRunning
                          : testRun.statusByNode[node.id] === 'success'
                            ? copy.labels.testSuccess
                            : copy.labels.testError
                      }}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      data-node-action
                      class="grid size-7 place-content-center rounded-md text-n-slate-9 transition hover:bg-n-alpha-2 hover:text-n-brand"
                      :title="copy.actions.editNode"
                      :aria-label="copy.actions.editNode"
                      @pointerdown.stop
                      @click.stop="openNodeInspector(node.id)"
                    >
                      <span class="i-lucide-panel-right-open size-4" />
                    </button>
                    <span class="i-lucide-grip size-4 text-n-slate-9" />
                  </div>
                </div>
                <p
                  class="mt-2 line-clamp-2 min-h-9 text-xs leading-5 text-n-slate-11"
                >
                  {{
                    node.type === 'trigger'
                      ? triggerSummary(node)
                      : node.description || actionSummary(node) || node.source
                  }}
                </p>
                <div
                  v-if="showNodeCounters"
                  class="absolute inset-x-0 top-full mt-2 flex flex-wrap gap-1.5"
                  data-testid="flow-node-counters"
                >
                  <span
                    v-for="item in nodeCounterItems(node)"
                    :key="item.key"
                    class="inline-flex items-center gap-1 rounded-md border border-n-weak bg-n-solid-1 px-2 py-0.5 text-[11px] shadow-sm"
                    :class="
                      item.key === 'potential'
                        ? 'text-n-teal-11'
                        : 'text-n-slate-11'
                    "
                    :title="item.help"
                  >
                    <span>{{ item.label }}</span>
                    <strong class="font-semibold text-n-slate-12">
                      {{ item.value }}
                    </strong>
                  </span>
                </div>
              </article>
            </div>
          </div>

          <div
            v-if="contextMenu.visible"
            class="fixed z-50 w-52 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm shadow-lg"
            :style="{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }"
            @click.stop
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('edit')"
            >
              <span class="i-lucide-panel-right-open size-4" />
              {{ copy.actions.editNode }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('connect')"
            >
              <span class="i-lucide-git-compare-arrows size-4" />
              {{ copy.actions.connect }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('duplicate')"
            >
              <span class="i-lucide-copy size-4" />
              {{ copy.actions.duplicate }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('open')"
            >
              <span class="i-lucide-external-link size-4" />
              {{ copy.actions.openLink }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-ruby-700 hover:bg-ruby-50"
              @click="runContextAction('remove')"
            >
              <span class="i-lucide-trash-2 size-4" />
              {{ copy.actions.remove }}
            </button>
          </div>
        </section>

        <aside
          class="flow-side-panel flex min-h-0 flex-col border-l border-n-weak bg-n-solid-1"
          :class="{ 'is-collapsed': isFlowPanelCollapsed }"
        >
          <div
            class="flow-start-panel min-h-0 overflow-y-auto border-b border-n-weak p-4"
          >
            <button
              type="button"
              class="mb-3 hidden size-9 place-content-center rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 lg:grid"
              :title="
                isFlowPanelCollapsed
                  ? copy.actions.expandPanel
                  : copy.actions.collapsePanel
              "
              :aria-label="
                isFlowPanelCollapsed
                  ? copy.actions.expandPanel
                  : copy.actions.collapsePanel
              "
              @click="isFlowPanelCollapsed = !isFlowPanelCollapsed"
            >
              <span
                class="size-4"
                :class="
                  isFlowPanelCollapsed
                    ? 'i-lucide-panel-right-open'
                    : 'i-lucide-panel-right-close'
                "
              />
            </button>
            <p class="text-sm font-semibold">{{ copy.labels.flowStart }}</p>
            <p class="mt-1 text-xs leading-5 text-n-slate-10">
              {{ copy.labels.flowStartDescription }}
            </p>
            <div class="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
              <button
                type="button"
                class="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                @click="addTriggerNode"
              >
                <span class="i-lucide-play size-4" />
                {{ copy.actions.addTrigger }}
              </button>
              <button
                type="button"
                class="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 text-sm font-medium text-emerald-200 hover:bg-emerald-500/25"
                @click="addWebhookTriggerNode"
              >
                <span class="i-lucide-radio-tower size-4" />
                {{ copy.actions.addWebhookTrigger }}
              </button>
            </div>
            <p class="mt-4 text-sm font-semibold">{{ copy.actions.addNode }}</p>
            <div class="mt-2">
              <Input
                v-model="panelShortcutSearch"
                type="search"
                size="sm"
                :placeholder="copy.labels.nodeShortcutSearch"
                :aria-label="copy.labels.nodeShortcutSearch"
              />
            </div>
            <div class="mt-3 grid gap-3 pb-6" data-testid="flow-node-shortcuts">
              <section
                v-for="group in panelShortcutGroups"
                :key="group.id"
                class="grid gap-1.5"
              >
                <p
                  class="mb-0 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                >
                  <span class="size-3.5" :class="[group.icon]" />
                  {{ group.label }}
                </p>
                <div
                  class="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-2"
                >
                  <button
                    v-for="shortcut in group.items"
                    :key="shortcut.id"
                    type="button"
                    class="flex min-h-9 items-center gap-2 rounded-lg border border-n-weak px-2 py-1.5 text-left text-xs font-medium text-n-slate-12 hover:border-n-brand hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :title="shortcut.description"
                    @click="addNodeFromShortcut(shortcut)"
                  >
                    <span class="size-4 shrink-0" :class="[shortcut.icon]" />
                    <span class="min-w-0 leading-4">{{ shortcut.title }}</span>
                  </button>
                </div>
              </section>
              <p
                v-if="!panelShortcutGroups.length"
                class="mb-0 text-xs text-n-slate-10"
              >
                {{ copy.labels.nodeShortcutEmpty }}
              </p>
            </div>
          </div>
        </aside>
      </div>

      <div
        v-if="selectedNode && isNodeInspectorOpen"
        class="node-editor-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="node-editor-title"
      >
        <div class="node-editor-backdrop" @click="closeNodeInspector" />
        <section class="node-editor-dialog">
          <header class="node-editor-header">
            <div class="flex items-start gap-3">
              <span
                class="grid size-10 shrink-0 place-content-center rounded-lg border"
                :class="nodeToneClass(selectedNode.color)"
              >
                <span
                  class="size-5"
                  :class="[nodeTypeMeta(selectedNode.type).icon]"
                />
              </span>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    class="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium"
                    :class="nodeToneClass(selectedNode.color)"
                  >
                    {{ nodeTypeMeta(selectedNode.type).label }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ copy.actions.editNode }}
                  </span>
                </div>
                <h2
                  id="node-editor-title"
                  class="mb-0 mt-1 truncate text-base font-semibold text-n-slate-12"
                  :title="selectedNode.title"
                >
                  {{ selectedNode.title || copy.actions.editNode }}
                </h2>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ copy.labels.editorSubtitle }}
                </p>
              </div>
              <NextButton
                type="button"
                size="sm"
                variant="ghost"
                color="slate"
                icon="i-hugeicons-cancel-01"
                :aria-label="copy.actions.close"
                :title="copy.actions.close"
                @click="closeNodeInspector"
              />
            </div>
            <nav
              class="node-flow-breadcrumb"
              :aria-label="copy.labels.processColumn"
              data-testid="node-flow-breadcrumb"
            >
              <div class="relative min-w-0 md:flex-1">
                <button
                  type="button"
                  class="node-flow-step w-full text-start"
                  :class="{ 'is-link': selectedIncomingNodes.length }"
                  :disabled="!selectedIncomingNodes.length"
                  :aria-haspopup="
                    selectedIncomingNodes.length > 1 ? 'menu' : undefined
                  "
                  :aria-expanded="breadcrumbMenu === 'in'"
                  :title="
                    selectedIncomingNodes.length
                      ? copy.labels.previousNode
                      : copy.labels.noPreviousNode
                  "
                  data-testid="node-breadcrumb-in"
                  @click="openBreadcrumbTarget('in')"
                >
                  <span class="i-hugeicons-login-03 size-3.5 shrink-0" />
                  <span class="node-flow-step-label">
                    {{ copy.labels.inputColumn }}
                  </span>
                  <strong>{{
                    selectedIncomingNodes.length === 1
                      ? selectedIncomingNodes[0].title
                      : selectedIncomingNodes.length > 1
                        ? `${selectedIncomingNodes.length} nós`
                        : selectedNode.type === 'trigger'
                          ? triggerSummary(selectedNode)
                          : selectedNode.source || copy.labels.noInbox
                  }}</strong>
                </button>
                <div
                  v-if="breadcrumbMenu === 'in'"
                  class="node-flow-menu"
                  role="menu"
                  data-testid="node-breadcrumb-menu"
                >
                  <p class="node-flow-menu-title">
                    {{ copy.labels.chooseNode }}
                  </p>
                  <button
                    v-for="item in selectedIncomingNodes"
                    :key="item.id"
                    type="button"
                    role="menuitem"
                    class="node-flow-menu-item"
                    @click="goToNode(item.id)"
                  >
                    <span
                      class="size-3.5 shrink-0"
                      :class="[nodeTypeMeta(item.type).icon]"
                    />
                    <span class="truncate">{{ item.title }}</span>
                  </button>
                </div>
              </div>
              <span
                class="i-hugeicons-arrow-right-01 size-3.5 shrink-0 text-n-slate-9"
                aria-hidden="true"
              />
              <span class="node-flow-step is-current md:flex-1">
                <span
                  class="i-hugeicons-workflow-square-03 size-3.5 shrink-0"
                />
                <span class="node-flow-step-label">
                  {{ copy.labels.thisNode }}
                </span>
                <strong>{{ nodeTypeMeta(selectedNode.type).label }}</strong>
              </span>
              <span
                class="i-hugeicons-arrow-right-01 size-3.5 shrink-0 text-n-slate-9"
                aria-hidden="true"
              />
              <div class="relative min-w-0 md:flex-1">
                <button
                  type="button"
                  class="node-flow-step w-full text-start"
                  :class="{ 'is-link': selectedOutgoingNodes.length }"
                  :disabled="!selectedOutgoingNodes.length"
                  :aria-haspopup="
                    selectedOutgoingNodes.length > 1 ? 'menu' : undefined
                  "
                  :aria-expanded="breadcrumbMenu === 'out'"
                  :title="
                    selectedOutgoingNodes.length
                      ? copy.labels.nextNode
                      : copy.labels.noNextNode
                  "
                  data-testid="node-breadcrumb-out"
                  @click="openBreadcrumbTarget('out')"
                >
                  <span class="i-hugeicons-logout-03 size-3.5 shrink-0" />
                  <span class="node-flow-step-label">
                    {{ copy.labels.outputColumn }}
                  </span>
                  <strong>{{
                    selectedOutgoingNodes.length === 1
                      ? selectedOutgoingNodes[0].title
                      : selectedOutgoingNodes.length > 1
                        ? `${selectedOutgoingNodes.length} nós`
                        : selectedNode.type === 'trigger'
                          ? copy.actions.addNextAction
                          : actionSummary(selectedNode)
                  }}</strong>
                </button>
                <div
                  v-if="breadcrumbMenu === 'out'"
                  class="node-flow-menu ltr:right-0 rtl:left-0"
                  role="menu"
                  data-testid="node-breadcrumb-menu"
                >
                  <p class="node-flow-menu-title">
                    {{ copy.labels.chooseNode }}
                  </p>
                  <button
                    v-for="item in selectedOutgoingNodes"
                    :key="item.id"
                    type="button"
                    role="menuitem"
                    class="node-flow-menu-item"
                    @click="goToNode(item.id)"
                  >
                    <span
                      class="size-3.5 shrink-0"
                      :class="[nodeTypeMeta(item.type).icon]"
                    />
                    <span class="truncate">{{ item.title }}</span>
                  </button>
                </div>
              </div>
            </nav>
            <div
              class="flex flex-wrap items-center gap-2"
              data-testid="node-navigation"
            >
              <NextButton
                type="button"
                size="sm"
                variant="faded"
                color="slate"
                icon="i-hugeicons-arrow-left-01"
                :label="copy.labels.previousNode"
                :disabled="!previousFlowNode"
                :title="copy.labels.navigationHint"
                data-testid="node-nav-previous"
                @click="previousFlowNode && goToNode(previousFlowNode.id)"
              />
              <NextButton
                type="button"
                size="sm"
                variant="faded"
                color="slate"
                icon="i-hugeicons-arrow-right-01"
                trailing-icon
                :label="copy.labels.nextNode"
                :disabled="!nextFlowNode"
                :title="copy.labels.navigationHint"
                data-testid="node-nav-next"
                @click="nextFlowNode && goToNode(nextFlowNode.id)"
              />
              <div class="min-w-0 flex-1 sm:max-w-xs">
                <ComboBox
                  :model-value="selectedNode.id"
                  :options="nodeJumpOptions"
                  :display-label="optionLabel(nodeJumpOptions, selectedNode.id)"
                  :placeholder="copy.labels.goToNode"
                  :search-placeholder="copy.labels.goToNodeSearch"
                  :empty-state="copy.labels.noResults"
                  :aria-label="copy.labels.goToNode"
                  teleport-to-body
                  data-testid="node-nav-jump"
                  @update:model-value="goToNode"
                />
              </div>
              <span class="hidden text-[11px] text-n-slate-10 lg:inline">
                {{ copy.labels.navigationHint }}
              </span>
            </div>
          </header>

          <div class="node-editor-body">
            <section class="node-editor-section">
              <div class="node-editor-section-heading">
                <span class="i-hugeicons-identity-card size-4" />
                <div>
                  <h3>{{ copy.labels.sectionIdentity }}</h3>
                  <p>{{ copy.labels.sectionIdentityHelp }}</p>
                </div>
              </div>
              <div class="grid gap-3 md:grid-cols-2">
                <Input
                  v-model="selectedNode.title"
                  :label="copy.form.nodeTitle"
                />
                <div class="node-field">
                  <span class="node-field-label">{{ copy.form.color }}</span>
                  <ComboBox
                    :model-value="selectedNode.color"
                    :options="cardPalette"
                    :display-label="
                      optionLabel(cardPalette, selectedNode.color)
                    "
                    :placeholder="copy.labels.selectPlaceholder"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="
                      assignWhenSelected(selectedNode, 'color', $event)
                    "
                  />
                </div>
                <div class="md:col-span-2">
                  <TextArea
                    v-model="selectedNode.description"
                    :label="copy.form.description"
                    :max-length="500"
                    auto-height
                    resize
                    min-height="4.5rem"
                  />
                </div>
              </div>
            </section>

            <section class="node-editor-section">
              <div class="node-editor-section-heading">
                <span class="i-hugeicons-settings-02 size-4" />
                <div>
                  <h3>{{ copy.labels.sectionBehavior }}</h3>
                  <p>{{ copy.labels.sectionBehaviorHelp }}</p>
                </div>
              </div>
              <div class="md:max-w-sm">
                <div class="node-field">
                  <span class="node-field-label">{{ copy.form.nodeType }}</span>
                  <ComboBox
                    :model-value="selectedNode.type"
                    :options="nodeTypes"
                    :display-label="optionLabel(nodeTypes, selectedNode.type)"
                    :placeholder="copy.labels.selectPlaceholder"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="
                      assignWhenSelected(selectedNode, 'type', $event)
                    "
                  />
                </div>
              </div>

              <section
                v-if="selectedNode.type === 'trigger'"
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <p class="text-sm font-semibold">
                  {{ copy.labels.triggerSettings }}
                </p>

                <div class="node-field">
                  <span class="node-field-label">{{
                    copy.form.triggerType
                  }}</span>
                  <ComboBox
                    :model-value="selectedNode.trigger.type"
                    :options="triggerOptions"
                    :display-label="
                      optionLabel(triggerOptions, selectedNode.trigger.type)
                    "
                    :placeholder="copy.labels.selectPlaceholder"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="
                      assignWhenSelected(selectedNode.trigger, 'type', $event)
                    "
                  />
                </div>

                <div
                  v-if="selectedNode.trigger.type === 'label_added'"
                  class="node-field"
                >
                  <span class="node-field-label">{{
                    copy.form.triggerLabel
                  }}</span>
                  <ComboBox
                    :model-value="selectedNode.trigger.label || ''"
                    :options="labelOptions"
                    :display-label="selectedNode.trigger.label || ''"
                    :placeholder="copy.labels.labelSelectPlaceholder"
                    :search-placeholder="copy.labels.labelSearch"
                    :empty-state="copy.labels.noLabels"
                    teleport-to-body
                    @update:model-value="selectedNode.trigger.label = $event"
                  />
                </div>

                <Input
                  v-if="selectedNode.trigger.type === 'message_contains'"
                  v-model="selectedNode.trigger.keyword"
                  :label="copy.form.triggerKeyword"
                  :placeholder="copy.labels.triggerKeywordPlaceholder"
                />

                <div
                  v-if="selectedNode.trigger.type === 'inbox_changed'"
                  class="node-field"
                >
                  <span class="node-field-label">{{
                    copy.form.triggerInbox
                  }}</span>
                  <ComboBox
                    :model-value="idValue(selectedNode.trigger.inboxId)"
                    :options="inboxOptions"
                    :placeholder="copy.labels.flowInboxPlaceholder"
                    :search-placeholder="copy.labels.inboxSearch"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="selectedNode.trigger.inboxId = $event"
                  />
                </div>

                <button
                  v-if="selectedNode.trigger.type !== 'webhook_received'"
                  type="button"
                  class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 text-sm font-medium text-emerald-200 hover:bg-emerald-500/25"
                  @click="useWebhookOnSelectedTrigger"
                >
                  <span class="i-lucide-radio-tower size-4" />
                  {{ copy.actions.useWebhookTrigger }}
                </button>

                <section
                  v-if="selectedNode.trigger.type === 'webhook_received'"
                  class="grid gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                >
                  <div class="grid gap-2 text-sm font-medium">
                    <span>{{ copy.form.triggerWebhookEndpoint }}</span>
                    <div class="flex gap-2">
                      <Input
                        :model-value="webhookEndpointFor(selectedNode)"
                        class="min-w-0 flex-1"
                        readonly
                        :aria-label="copy.form.triggerWebhookEndpoint"
                      />
                      <button
                        type="button"
                        class="grid size-10 shrink-0 place-content-center rounded-lg border border-n-weak hover:border-n-brand hover:text-n-brand"
                        :title="copy.actions.copyWebhookUrl"
                        :aria-label="copy.actions.copyWebhookUrl"
                        @click="copyWebhookEndpoint(selectedNode)"
                      >
                        <span class="i-lucide-copy size-4" />
                      </button>
                      <button
                        type="button"
                        class="grid size-10 shrink-0 place-content-center rounded-lg border border-n-weak hover:border-n-brand hover:text-n-brand"
                        :title="copy.actions.refreshWebhook"
                        :aria-label="copy.actions.refreshWebhook"
                        @click="refreshCurrentFunnel"
                      >
                        <span class="i-lucide-refresh-cw size-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 text-sm font-medium text-emerald-200 hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="testRun.isRunning"
                    @click="waitForWebhookAndTest(selectedNode)"
                  >
                    <span
                      class="size-4"
                      :class="
                        testRun.isRunning
                          ? 'i-lucide-loader-2 animate-spin'
                          : 'i-lucide-satellite-dish'
                      "
                    />
                    {{ copy.actions.waitWebhookTest }}
                  </button>

                  <div class="node-field">
                    <span class="node-field-label">
                      {{ copy.form.triggerWebhookMethod }}
                    </span>
                    <ComboBox
                      :model-value="selectedNode.trigger.webhookMethod"
                      :options="webhookMethodOptions"
                      :display-label="selectedNode.trigger.webhookMethod"
                      :placeholder="copy.labels.selectPlaceholder"
                      :search-placeholder="copy.labels.searchPlaceholder"
                      :empty-state="copy.labels.noResults"
                      teleport-to-body
                      @update:model-value="
                        assignWhenSelected(
                          selectedNode.trigger,
                          'webhookMethod',
                          $event
                        )
                      "
                    />
                  </div>

                  <TextArea
                    v-model="selectedNode.trigger.webhookSample"
                    :label="copy.form.triggerWebhookSample"
                    :max-length="20000"
                    auto-height
                    resize
                    min-height="8rem"
                    max-height="20rem"
                    custom-text-area-class="font-mono !text-xs"
                  />

                  <div class="grid gap-3 sm:grid-cols-3">
                    <Input
                      v-model="selectedNode.trigger.contactPath"
                      :label="copy.form.jsonContactPath"
                      :placeholder="copy.labels.jsonPathPlaceholder"
                    />
                    <Input
                      v-model="selectedNode.trigger.phonePath"
                      :label="copy.form.jsonPhonePath"
                      :placeholder="copy.labels.jsonPathPlaceholder"
                    />
                    <Input
                      v-model="selectedNode.trigger.messagePath"
                      :label="copy.form.jsonMessagePath"
                      :placeholder="copy.labels.jsonPathPlaceholder"
                    />
                  </div>

                  <div
                    class="rounded-lg border border-dashed border-n-weak p-3"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <p class="text-xs font-medium text-n-slate-10">
                        {{
                          selectedWebhookLastFields.length
                            ? copy.labels.jsonLastFields
                            : copy.labels.jsonDetectedFields
                        }}
                      </p>
                      <span
                        v-if="selectedNode.trigger.lastReceivedAt"
                        class="text-[11px] text-n-slate-10"
                      >
                        {{ selectedNode.trigger.lastReceivedAt }}
                      </span>
                    </div>
                    <div
                      v-if="selectedWebhookFieldOptions.length"
                      class="mt-2 flex flex-wrap gap-2"
                    >
                      <button
                        v-for="field in selectedWebhookFieldOptions"
                        :key="field"
                        type="button"
                        class="rounded-md border border-n-weak px-2 py-1 font-mono text-[11px] text-n-slate-11 hover:border-n-brand hover:text-n-brand"
                        @click="selectedNode.trigger.messagePath = field"
                      >
                        {{ field }}
                      </button>
                    </div>
                    <p v-else class="mt-2 text-xs text-n-slate-10">
                      {{ copy.labels.webhookNoPayload }}
                    </p>
                  </div>

                  <div v-if="selectedWebhookPayloadPreview" class="node-field">
                    <span class="node-field-label">
                      {{ copy.form.triggerWebhookLastPayload }}
                    </span>
                    <div
                      class="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 font-mono text-xs text-n-slate-12"
                      tabindex="0"
                    >
                      {{ selectedWebhookPayloadPreview }}
                    </div>
                  </div>
                </section>

                <button
                  type="button"
                  class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                  @click="addConnectedNodeAfter(selectedNode, 'action')"
                >
                  <span class="i-lucide-plus size-4" />
                  {{ copy.actions.addNextAction }}
                </button>
              </section>

              <section
                v-if="selectedNode.type !== 'trigger'"
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <p class="text-sm font-semibold">
                  {{
                    selectedNode.type === 'decision'
                      ? copy.labels.conditionSettings
                      : copy.labels.actionSettings
                  }}
                </p>

                <ConditionRulesEditor
                  v-if="selectedNode.type === 'decision'"
                  :condition="selectedNode.action.condition"
                  :field-options="selectedConditionFieldOptions"
                  @update="updateSelectedCondition"
                />

                <div
                  v-if="showsActionSelector(selectedNode)"
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.form.actionType }}
                  </span>
                  <ComboBox
                    :model-value="selectedNode.action.type"
                    :options="actionTypeOptions"
                    :display-label="
                      optionLabel(actionTypeOptions, selectedNode.action.type)
                    "
                    :placeholder="copy.labels.selectPlaceholder"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="selectActionType"
                  />
                </div>

                <HttpRequestEditor
                  v-if="selectedNode.action.type === 'http_request'"
                  :key="`http-${selectedNode.id}`"
                  :action="selectedNode.action"
                  :funnel-id="currentFunnelId"
                  :upstream-variables="selectedUpstreamHttpVariables"
                  :samples="selectedHttpSamples"
                  :can-test="canTestHttpRequest"
                  @update="patchSelectedAction"
                />

                <section
                  v-if="selectedNode.action.type === 'send_whatsapp_flow'"
                  class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 sm:grid-cols-2"
                  data-testid="funnel-whatsapp-flow-fields"
                >
                  <p
                    v-if="!cloudInboxOptions.length"
                    class="mb-0 rounded-lg bg-n-amber-2 p-3 text-xs text-n-amber-12 sm:col-span-2"
                  >
                    {{ copy.labels.flowNoCloudInbox }}
                  </p>
                  <div class="node-field">
                    <span class="node-field-label">
                      {{ copy.labels.flowInbox }}
                    </span>
                    <ComboBox
                      :model-value="idValue(selectedNode.action.flowInboxId)"
                      :options="cloudInboxOptions"
                      :placeholder="copy.labels.flowInboxPlaceholder"
                      :search-placeholder="copy.labels.inboxSearch"
                      :empty-state="copy.labels.flowNoCloudInbox"
                      :disabled="!cloudInboxOptions.length"
                      teleport-to-body
                      data-testid="funnel-whatsapp-flow-inbox"
                      @update:model-value="selectWhatsappFlowInbox"
                    />
                  </div>
                  <div class="node-field">
                    <span class="node-field-label">
                      {{ copy.labels.flowSelect }}
                    </span>
                    <div class="flex items-center gap-2">
                      <ComboBox
                        :model-value="selectedNode.action.flowId"
                        :options="selectedWhatsappFlowOptions"
                        :display-label="selectedWhatsappFlow?.name || ''"
                        :placeholder="copy.labels.flowSelectPlaceholder"
                        :search-placeholder="copy.labels.flowSearch"
                        :empty-state="copy.labels.flowEmpty"
                        :disabled="
                          !selectedNode.action.flowInboxId ||
                          isLoadingWhatsappFlows
                        "
                        teleport-to-body
                        class="min-w-0 flex-1"
                        @update:model-value="selectWhatsappFlow"
                      />
                      <NextButton
                        type="button"
                        size="sm"
                        variant="ghost"
                        color="slate"
                        icon="i-hugeicons-refresh"
                        :is-loading="isLoadingWhatsappFlows"
                        :aria-label="copy.actions.refreshWhatsappFlows"
                        :title="copy.actions.refreshWhatsappFlows"
                        @click="loadWhatsappFlows"
                      />
                    </div>
                  </div>
                  <div
                    v-if="selectedWhatsappFlow?.status === 'DRAFT'"
                    class="grid gap-2 rounded-lg bg-n-amber-2 p-3 text-xs text-n-amber-12 sm:col-span-2"
                  >
                    <p class="mb-0 flex items-start gap-2">
                      <span class="i-hugeicons-alert-02 size-4 shrink-0" />
                      {{ copy.labels.flowDraftWarning }}
                    </p>
                    <label
                      class="flex cursor-pointer items-center gap-2 font-medium"
                    >
                      <Checkbox
                        :model-value="selectedNode.action.flowMode === 'draft'"
                        @change="toggleWhatsappFlowDraftMode"
                      />
                      {{ copy.labels.flowDraftConfirm }}
                    </label>
                  </div>
                  <Input
                    v-model="selectedNode.action.flowCta"
                    :label="copy.labels.flowCta"
                    maxlength="20"
                    :placeholder="copy.labels.flowCtaPlaceholder"
                  />
                  <Input
                    :model-value="
                      selectedNode.action.flowScreen ||
                      selectedWhatsappFlow?.first_screen_id ||
                      ''
                    "
                    :label="copy.labels.flowScreen"
                    :message="copy.labels.flowScreenHelp"
                    readonly
                    :title="copy.labels.flowScreenHelp"
                  />
                  <div class="sm:col-span-2">
                    <TextArea
                      v-model="selectedNode.action.flowBody"
                      :label="copy.labels.flowBody"
                      :placeholder="copy.labels.flowBodyPlaceholder"
                      :max-length="1024"
                      show-character-count
                      auto-height
                      resize
                      min-height="5rem"
                    />
                  </div>
                  <Input
                    v-model="selectedNode.action.flowHeader"
                    :label="copy.labels.flowHeader"
                    maxlength="60"
                  />
                  <Input
                    v-model="selectedNode.action.flowFooter"
                    :label="copy.labels.flowFooter"
                    maxlength="60"
                  />
                  <p
                    class="mb-0 flex items-start gap-2 text-xs leading-5 text-n-slate-11 sm:col-span-2"
                  >
                    <span
                      class="i-hugeicons-information-circle size-4 shrink-0"
                    />
                    {{ copy.labels.flowRules }}
                  </p>
                  <div class="sm:col-span-2">
                    <NextButton
                      type="button"
                      size="sm"
                      variant="faded"
                      color="slate"
                      icon="i-hugeicons-workflow-square-06"
                      :label="copy.actions.manageWhatsappFlows"
                      @click="openWhatsappFlowsPage"
                    />
                  </div>
                </section>

                <TextArea
                  v-if="
                    ['send_message', 'send_file'].includes(
                      selectedNode.action.type
                    )
                  "
                  v-model="selectedNode.action.message"
                  :label="copy.form.actionMessage"
                  :placeholder="copy.labels.jsonVariablesHint"
                  :max-length="4096"
                  auto-height
                  resize
                  min-height="5rem"
                />

                <TextArea
                  v-if="selectedNode.action.type === 'add_private_note'"
                  v-model="selectedNode.action.message"
                  :label="copy.labels.privateNoteField"
                  :placeholder="copy.labels.privateNotePlaceholder"
                  :max-length="4096"
                  auto-height
                  resize
                  min-height="5rem"
                />

                <section
                  v-if="selectedNode.action.type === 'send_message'"
                  class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <div>
                    <p class="text-sm font-semibold">
                      {{ copy.labels.sendMessageTarget }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ copy.labels.sendMessageTargetHelp }}
                    </p>
                  </div>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <Input
                      v-model="selectedNode.action.phonePath"
                      :label="copy.form.actionPhonePath"
                      :placeholder="copy.labels.jsonPathPlaceholder"
                    />
                    <Input
                      v-model="selectedNode.action.contactPath"
                      :label="copy.form.actionContactPath"
                      :placeholder="copy.labels.jsonPathPlaceholder"
                    />
                    <Input
                      v-model="selectedNode.action.messagePath"
                      :label="copy.form.actionMessagePath"
                      :placeholder="copy.labels.jsonPathPlaceholder"
                    />
                  </div>
                  <div
                    v-if="selectedActionWebhookFieldOptions.length"
                    class="flex flex-wrap gap-2"
                  >
                    <button
                      v-for="field in selectedActionWebhookFieldOptions"
                      :key="field"
                      type="button"
                      class="rounded-md border border-n-weak px-2 py-1 font-mono text-[11px] text-n-slate-11 hover:border-n-brand hover:text-n-brand"
                      @click="insertActionJsonVariable(field)"
                    >
                      {{ field }}
                    </button>
                  </div>
                </section>

                <NodeFileField
                  v-if="selectedNode.action.type === 'send_file'"
                  :label="copy.form.actionFile"
                  :file="selectedNode.action.file"
                  :is-uploading="isUploadingFile"
                  :max-size-mb="maxUploadSizeMb"
                  @select="uploadActionFile"
                  @remove="removeActionFile"
                  @invalid="message => useAlert(message)"
                />

                <Input
                  v-if="selectedNode.action.type === 'send_file'"
                  v-model="selectedNode.action.fileCaption"
                  :label="copy.form.actionFileCaption"
                  :placeholder="copy.labels.fileCaptionPlaceholder"
                />

                <Input
                  v-if="selectedNode.action.type === 'call_webhook'"
                  v-model="selectedNode.action.webhookUrl"
                  :label="copy.form.webhookUrl"
                  :placeholder="copy.labels.webhookPlaceholder"
                />

                <div
                  v-if="selectedNode.action.type === 'call_webhook'"
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.form.webhookMethod }}
                  </span>
                  <ComboBox
                    :model-value="selectedNode.action.webhookMethod"
                    :options="webhookMethodOptions"
                    :display-label="selectedNode.action.webhookMethod"
                    :placeholder="copy.labels.selectPlaceholder"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="
                      assignWhenSelected(
                        selectedNode.action,
                        'webhookMethod',
                        $event
                      )
                    "
                  />
                </div>

                <div
                  v-if="selectedNode.action.type === 'run_credit_engine'"
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.form.creditEngine }}
                  </span>
                  <ComboBox
                    :model-value="idValue(selectedNode.action.creditEngineId)"
                    :options="creditEngineOptions"
                    :placeholder="copy.labels.noCreditEngine"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noCreditEngines"
                    teleport-to-body
                    @update:model-value="
                      selectedNode.action.creditEngineId = $event
                    "
                  />
                  <span
                    v-if="!creditEngineOptions.length"
                    class="text-xs text-n-slate-10"
                  >
                    {{ copy.labels.noCreditEngines }}
                  </span>
                </div>

                <div
                  v-if="selectedNode.action.type === 'run_credit_engine'"
                  class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <p class="mb-0 text-xs leading-5 text-n-slate-11">
                    {{ copy.labels.creditEngineHelp }}
                  </p>
                  <div
                    v-if="selectedCreditEngine"
                    class="grid gap-1 rounded-lg bg-n-alpha-2 p-3 text-xs"
                  >
                    <strong class="text-n-slate-12">
                      {{ selectedCreditEngineLabel }}
                    </strong>
                    <span class="text-n-slate-10">
                      {{ selectedCreditEngine.eligibility_summary }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                    @click="openCreditEngineSettings"
                  >
                    <span class="i-lucide-settings-2 size-4" />
                    {{ copy.labels.openCreditEngine }}
                  </button>
                </div>

                <div
                  v-if="
                    ['add_label', 'remove_label'].includes(
                      selectedNode.action.type
                    )
                  "
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.labels.labelField }}
                  </span>
                  <ComboBox
                    :model-value="selectedNode.action.label || ''"
                    :options="labelOptions"
                    :display-label="selectedNode.action.label || ''"
                    :placeholder="copy.labels.labelSelectPlaceholder"
                    :search-placeholder="copy.labels.labelSearch"
                    :empty-state="copy.labels.noLabels"
                    teleport-to-body
                    @update:model-value="selectedNode.action.label = $event"
                  />
                </div>

                <div
                  v-if="selectedNode.action.type === 'assign_team'"
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.labels.teamField }}
                  </span>
                  <ComboBox
                    :model-value="idValue(selectedNode.action.teamId)"
                    :options="teamOptions"
                    :placeholder="copy.labels.teamPlaceholder"
                    :search-placeholder="copy.labels.teamSearch"
                    :empty-state="copy.labels.noTeams"
                    teleport-to-body
                    @update:model-value="selectedNode.action.teamId = $event"
                  />
                </div>

                <div
                  v-if="selectedNode.action.type === 'set_priority'"
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.labels.priorityField }}
                  </span>
                  <ComboBox
                    :model-value="selectedNode.action.priority || ''"
                    :options="PRIORITY_OPTIONS"
                    :placeholder="copy.labels.priorityPlaceholder"
                    :search-placeholder="copy.labels.searchPlaceholder"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="selectedNode.action.priority = $event"
                  />
                </div>

                <div
                  v-if="selectedNode.action.type === 'move_inbox'"
                  class="node-field"
                >
                  <span class="node-field-label">
                    {{ copy.labels.targetInbox }}
                  </span>
                  <ComboBox
                    :model-value="idValue(selectedNode.action.targetInboxId)"
                    :options="inboxOptions"
                    :placeholder="copy.labels.flowInboxPlaceholder"
                    :search-placeholder="copy.labels.inboxSearch"
                    :empty-state="copy.labels.noResults"
                    teleport-to-body
                    @update:model-value="
                      selectedNode.action.targetInboxId = $event
                    "
                  />
                </div>

                <Input
                  v-if="
                    selectedNode.type === 'wait' ||
                    [
                      'send_message',
                      'send_file',
                      'request_best_offer',
                      'run_credit_engine',
                      'send_whatsapp_flow',
                      'http_request',
                    ].includes(selectedNode.action.type)
                  "
                  v-model.number="selectedNode.action.delayMinutes"
                  type="number"
                  min="0"
                  :label="copy.form.actionDelay"
                />
              </section>
            </section>

            <section class="node-editor-section">
              <div class="node-editor-section-heading">
                <span class="i-hugeicons-link-square-02 size-4" />
                <div>
                  <h3>{{ copy.labels.sectionShortcut }}</h3>
                  <p>{{ copy.labels.sectionShortcutHelp }}</p>
                </div>
              </div>
              <div class="grid gap-3 md:grid-cols-2">
                <Input
                  v-model="selectedNode.source"
                  :label="copy.form.source"
                />
                <div class="node-field">
                  <span class="node-field-label">
                    {{ copy.form.linkType }}
                  </span>
                  <div class="flex items-center gap-2">
                    <ComboBox
                      :model-value="selectedNode.linkType"
                      :options="linkOptions"
                      :display-label="
                        optionLabel(linkOptions, selectedNode.linkType)
                      "
                      :placeholder="copy.labels.selectPlaceholder"
                      :search-placeholder="copy.labels.searchPlaceholder"
                      :empty-state="copy.labels.noResults"
                      teleport-to-body
                      class="min-w-0 flex-1"
                      @update:model-value="
                        assignWhenSelected(selectedNode, 'linkType', $event)
                      "
                    />
                    <NextButton
                      type="button"
                      size="sm"
                      variant="faded"
                      color="slate"
                      icon="i-hugeicons-link-square-02"
                      :aria-label="copy.actions.openLink"
                      :title="copy.actions.openLink"
                      :disabled="!routeForLinkType(selectedNode.linkType)"
                      @click="openLinkedResource(selectedNode)"
                    />
                  </div>
                </div>
              </div>
            </section>

            <details class="node-editor-section group">
              <summary class="node-editor-section-heading cursor-pointer">
                <span class="i-hugeicons-chart-column size-4" />
                <div class="flex-1">
                  <h3>{{ copy.labels.sectionMetrics }}</h3>
                  <p>{{ copy.labels.sectionMetricsHelp }}</p>
                </div>
                <span
                  class="i-hugeicons-arrow-down-01 size-4 text-n-slate-10 transition-transform group-open:rotate-180"
                />
              </summary>
              <div class="mt-3 grid gap-3" data-testid="node-real-metrics">
                <p class="mb-0 text-xs font-semibold uppercase text-n-slate-10">
                  {{ copy.metrics.realMetrics }} · {{ metricsPeriodLabel }}
                </p>
                <div class="grid gap-2 sm:grid-cols-4">
                  <div class="rounded-lg bg-n-alpha-2 px-3 py-2">
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ copy.metrics.nodeLeads }}
                    </p>
                    <p class="mb-0 text-sm font-semibold">
                      {{
                        formatMetricCount(
                          nodeMetrics(selectedNode)?.leads ??
                            (flowMetrics ? 0 : null)
                        )
                      }}
                    </p>
                  </div>
                  <div class="rounded-lg bg-n-alpha-2 px-3 py-2">
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ copy.form.conversion }}
                    </p>
                    <p class="mb-0 text-sm font-semibold">
                      {{
                        formatMetricPercent(
                          nodeMetrics(selectedNode)?.conversion
                        )
                      }}
                    </p>
                  </div>
                  <div class="rounded-lg bg-n-alpha-2 px-3 py-2">
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ copy.metrics.executions }}
                    </p>
                    <p class="mb-0 text-sm font-semibold">
                      {{
                        formatMetricCount(
                          nodeMetrics(selectedNode)?.executions ??
                            (flowMetrics ? 0 : null)
                        )
                      }}
                    </p>
                  </div>
                  <div class="rounded-lg bg-n-alpha-2 px-3 py-2">
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ copy.metrics.lastExecution }}
                    </p>
                    <p class="mb-0 text-sm font-semibold">
                      {{
                        nodeMetrics(selectedNode)?.last_executed_at
                          ? new Date(
                              nodeMetrics(selectedNode).last_executed_at
                            ).toLocaleString('pt-BR')
                          : '—'
                      }}
                    </p>
                  </div>
                </div>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ copy.metrics.nodeLeadsHelp }}
                  {{ copy.metrics.nodeConversionHelp }}
                </p>
                <div class="grid gap-3 sm:grid-cols-2">
                  <Input
                    v-model="selectedNodePotentialDisplay"
                    inputmode="decimal"
                    :label="copy.form.potential"
                    :message="copy.metrics.nodePotentialHelp"
                  />
                  <Input
                    v-model="selectedNodeInvestmentDisplay"
                    inputmode="decimal"
                    :label="copy.form.investment"
                  />
                </div>
              </div>
            </details>
          </div>

          <footer class="node-editor-footer">
            <div
              v-if="isConfirmingNodeRemoval"
              class="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              role="alert"
            >
              <p class="mb-0 flex items-start gap-2 text-sm text-n-ruby-11">
                <span class="i-hugeicons-alert-02 size-4 shrink-0" />
                {{ copy.labels.removeNodeConfirm }}
              </p>
              <div class="flex shrink-0 justify-end gap-2">
                <NextButton
                  type="button"
                  size="sm"
                  variant="faded"
                  color="slate"
                  :label="copy.actions.cancel"
                  @click="isConfirmingNodeRemoval = false"
                />
                <NextButton
                  type="button"
                  size="sm"
                  color="ruby"
                  icon="i-hugeicons-delete-02"
                  :label="copy.actions.confirmRemoveNode"
                  @click="confirmSelectedNodeRemoval"
                />
              </div>
            </div>
            <template v-else>
              <NextButton
                type="button"
                size="sm"
                variant="ghost"
                color="ruby"
                icon="i-hugeicons-delete-02"
                :label="copy.actions.removeNode"
                @click="isConfirmingNodeRemoval = true"
              />
              <div class="flex flex-wrap items-center justify-end gap-2">
                <span
                  class="hidden items-center gap-1.5 text-xs sm:inline-flex"
                  :class="saveStatusClass"
                >
                  <span class="i-hugeicons-cloud size-3.5" />
                  {{ saveStatusLabel }}
                </span>
                <NextButton
                  type="button"
                  size="sm"
                  variant="faded"
                  color="slate"
                  icon="i-hugeicons-copy-01"
                  :label="copy.actions.duplicate"
                  @click="duplicateNode(selectedNode)"
                />
                <NextButton
                  type="button"
                  size="sm"
                  icon="i-hugeicons-tick-02"
                  :label="copy.actions.done"
                  @click="closeNodeInspector"
                />
              </div>
            </template>
          </footer>
        </section>
      </div>
    </section>
    <div
      v-if="nodePicker.visible"
      class="fixed inset-0 z-50 grid place-items-center bg-n-slate-12/50 px-4 py-6"
      @click.self="closeNodePicker"
    >
      <section
        class="flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-2xl"
        data-testid="flow-next-node-picker"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <div class="min-w-0">
            <p class="text-lg font-semibold">{{ copy.labels.nextNodeTitle }}</p>
            <p class="mt-1 text-sm text-n-slate-10">
              {{ copy.labels.nextNodeDescription }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-9 place-content-center rounded-lg border border-n-weak hover:bg-n-alpha-2"
            :aria-label="copy.actions.close"
            @click="closeNodePicker"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </header>

        <div class="min-h-0 overflow-y-auto p-5">
          <Input
            v-model="nodePicker.search"
            type="search"
            :placeholder="copy.labels.nextNodeSearch"
            :aria-label="copy.labels.nextNodeSearch"
          />

          <div class="mt-4 grid gap-4">
            <section
              v-for="group in nextNodeShortcutGroups"
              :key="group.id"
              class="grid gap-2"
            >
              <p
                class="mb-0 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                <span class="size-3.5" :class="[group.icon]" />
                {{ group.label }}
              </p>
              <button
                v-for="option in group.items"
                :key="option.id"
                type="button"
                class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition hover:border-n-brand hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                @click="addNodeFromPicker(option)"
              >
                <span
                  class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-brand"
                >
                  <span class="size-5" :class="[option.icon]" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold">{{
                    option.title
                  }}</span>
                  <span class="mt-1 block text-xs leading-5 text-n-slate-10">
                    {{ option.description }}
                  </span>
                </span>
                <span
                  class="i-hugeicons-arrow-right-01 size-4 text-n-slate-10"
                />
              </button>
            </section>
            <p
              v-if="!nextNodeShortcutGroups.length"
              class="mb-0 text-sm text-n-slate-10"
            >
              {{ copy.labels.nodeShortcutEmpty }}
            </p>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="testRun.isModalOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-n-slate-12/40 px-4 py-6 backdrop-blur-sm"
      @click.self="closeTestModal"
    >
      <section
        class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl dark:border-n-strong"
        role="dialog"
        aria-modal="true"
        :aria-label="copy.labels.testRun"
      >
        <header
          class="flex items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
              aria-hidden="true"
            >
              <span class="i-hugeicons-play-circle size-5" />
            </span>
            <div class="min-w-0">
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                {{ copy.labels.testRun }}
              </h2>
              <p class="mb-0 mt-0.5 text-xs text-n-slate-11">
                {{ copy.labels.testModalDescription }}
              </p>
            </div>
          </div>
          <NextButton
            xs
            ghost
            slate
            icon="i-hugeicons-cancel-01"
            :aria-label="copy.actions.close"
            @click="closeTestModal"
          />
        </header>

        <div
          v-if="testRun.logs.length"
          class="flex flex-wrap items-center gap-2 border-b border-n-weak bg-n-solid-2 px-5 py-2.5 text-xs"
          aria-live="polite"
        >
          <span
            class="inline-flex items-center gap-1.5 rounded-md bg-n-teal-3 px-2 py-1 font-medium text-n-teal-11"
          >
            <span class="i-hugeicons-checkmark-circle-02 size-3.5" />
            {{ testRun.logs.filter(log => log.status !== 'error').length }}
            {{ copy.labels.testStepsOk }}
          </span>
          <span
            v-if="testRun.logs.some(log => log.status === 'error')"
            class="inline-flex items-center gap-1.5 rounded-md bg-n-ruby-3 px-2 py-1 font-medium text-n-ruby-11"
          >
            <span class="i-hugeicons-alert-circle size-3.5" />
            {{ testRun.logs.filter(log => log.status === 'error').length }}
            {{ copy.labels.testStepsError }}
          </span>
          <span
            v-if="testRun.isRunning"
            class="inline-flex items-center gap-1.5 text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full bg-n-amber-9 motion-safe:animate-pulse"
            />
            {{ copy.labels.testRunning }}
          </span>
        </div>

        <div class="min-h-0 overflow-y-auto px-5 py-4">
          <div
            v-if="!testRun.logs.length"
            class="rounded-xl border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
          >
            {{ copy.labels.testIdle }}
          </div>
          <ol v-else class="relative mb-0 grid list-none gap-3 ps-0">
            <li
              v-for="(log, index) in testRun.logs"
              :key="log.id"
              class="relative flex gap-3"
            >
              <span
                class="relative z-[1] grid size-7 shrink-0 place-items-center rounded-full border text-xs font-semibold tabular-nums"
                :class="
                  log.status === 'error'
                    ? 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11'
                    : 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
                "
                aria-hidden="true"
              >
                {{ index + 1 }}
              </span>
              <span
                v-if="index < testRun.logs.length - 1"
                class="absolute bottom-[-0.75rem] top-7 w-px bg-n-weak ltr:left-3.5 rtl:right-3.5"
                aria-hidden="true"
              />
              <article
                class="min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 dark:border-n-strong"
              >
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <h3
                    class="mb-0 min-w-0 truncate text-sm font-semibold text-n-slate-12"
                    :title="log.title"
                  >
                    {{ log.title }}
                  </h3>
                  <span
                    class="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium"
                    :class="
                      log.status === 'error'
                        ? 'bg-n-ruby-3 text-n-ruby-11'
                        : 'bg-n-teal-3 text-n-teal-11'
                    "
                  >
                    <span
                      class="size-3.5"
                      :class="
                        log.status === 'error'
                          ? 'i-hugeicons-alert-circle'
                          : 'i-hugeicons-checkmark-circle-02'
                      "
                    />
                    {{
                      log.status === 'error'
                        ? copy.labels.testStepError
                        : copy.labels.testStepOk
                    }}
                  </span>
                </div>
                <p class="mb-0 mt-1 text-sm text-n-slate-11">
                  {{ log.detail }}
                </p>
                <p v-if="log.summary" class="mb-0 mt-1 text-xs text-n-slate-11">
                  {{ log.summary }}
                </p>
                <p
                  v-if="log.hint"
                  class="mb-0 mt-2 rounded-lg bg-n-ruby-3 px-3 py-2 text-xs font-medium text-n-ruby-11"
                >
                  {{ log.hint }}
                </p>
              </article>
            </li>
          </ol>
        </div>

        <footer
          class="flex flex-wrap justify-end gap-2 border-t border-n-weak px-5 py-4"
        >
          <NextButton
            sm
            faded
            slate
            icon="i-hugeicons-reload"
            :label="copy.actions.clearTest"
            @click="clearTestRun"
          />
          <NextButton
            sm
            icon="i-hugeicons-play-circle"
            :label="copy.actions.testFlow"
            :is-loading="testRun.isRunning"
            @click="runTestFlow()"
          />
        </footer>
      </section>
    </div>

    <div
      v-if="edgeEditor.visible && editedEdge"
      class="fixed inset-0 z-50 grid place-items-center bg-n-slate-12/40 px-4 py-6"
      @click.self="closeEdgeEditor"
    >
      <section
        class="grid w-full max-w-md gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="flow-edge-editor-title"
        data-testid="flow-edge-editor"
      >
        <header class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2
              id="flow-edge-editor-title"
              class="mb-0 text-base font-semibold text-n-slate-12"
            >
              {{ copy.labels.edgeEditorTitle }}
            </h2>
            <p class="mb-0 mt-0.5 text-xs text-n-slate-11">
              {{ editedEdgeSource?.title }}
              <span class="i-hugeicons-arrow-right-01 size-3 align-middle" />
              {{
                flow.nodes.find(node => node.id === editedEdge.target)?.title
              }}
            </p>
          </div>
          <NextButton
            type="button"
            size="sm"
            variant="ghost"
            color="slate"
            icon="i-hugeicons-cancel-01"
            :aria-label="copy.actions.close"
            @click="closeEdgeEditor"
          />
        </header>
        <p class="mb-0 text-xs leading-5 text-n-slate-11">
          {{ copy.labels.edgeEditorDescription }}
        </p>
        <div v-if="editedEdgeBranches.length" class="grid gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ copy.labels.edgeBranches }}
          </span>
          <div class="flex flex-wrap gap-2">
            <NextButton
              v-for="branch in editedEdgeBranches"
              :key="branch"
              type="button"
              size="sm"
              :variant="editedEdge.label === branch ? 'solid' : 'faded'"
              :color="editedEdge.label === branch ? 'blue' : 'slate'"
              :label="branch"
              :aria-pressed="editedEdge.label === branch"
              data-testid="flow-edge-branch"
              @click="setEditedEdgeLabel(branch)"
            />
          </div>
        </div>
        <p v-else class="mb-0 text-xs text-n-slate-10">
          {{ copy.labels.edgeNoBranchHint }}
        </p>
        <Input
          :model-value="editedEdge.label"
          :label="copy.labels.edgeCustomLabel"
          :placeholder="copy.labels.edgePlaceholder"
          @update:model-value="setEditedEdgeLabel"
        />
        <footer class="flex flex-wrap items-center justify-between gap-2">
          <NextButton
            type="button"
            size="sm"
            variant="ghost"
            color="ruby"
            icon="i-hugeicons-delete-02"
            :label="copy.labels.removeEdge"
            @click="removeEditedEdge"
          />
          <NextButton
            type="button"
            size="sm"
            icon="i-hugeicons-tick-02"
            :label="copy.actions.done"
            @click="closeEdgeEditor"
          />
        </footer>
      </section>
    </div>

    <Dialog
      ref="pendingNavigationDialog"
      type="edit"
      solid
      width="md"
      :title="copy.labels.pendingTitle"
      :description="copy.labels.pendingDescription"
      :confirm-button-label="copy.labels.pendingConfirm"
      :cancel-button-label="copy.actions.cancel"
      :is-loading="isSavingBeforeNavigation"
      @confirm="confirmPendingNavigation"
      @close="cancelPendingNavigation"
    />
  </main>
</template>

<style scoped>
.node-field {
  @apply grid min-w-0 content-start gap-1;
}

.node-field-label {
  @apply mb-0.5 text-sm font-medium text-n-slate-12;
}

.flow-header-field {
  @apply grid gap-1;
}

.flow-header-field-label {
  @apply text-xs font-medium text-n-slate-10;
}

@media (prefers-reduced-motion: reduce) {
  [data-node-output],
  .flow-link-target {
    transition: none !important;
  }
}

.flow-side-panel.is-collapsed {
  @apply items-center;
}

.flow-side-panel.is-collapsed .flow-start-panel {
  @apply w-full border-b-0 p-2;
}

.flow-side-panel.is-collapsed .flow-start-panel > :not(button) {
  @apply hidden;
}

.flow-side-panel.is-collapsed .flow-start-panel > button {
  @apply mx-auto mb-0;
}

.node-editor-modal {
  @apply fixed inset-0 z-50;
}

.node-editor-backdrop {
  @apply absolute inset-0 backdrop-blur-sm;
  background: rgba(0, 0, 0, 0.6);
}

.node-editor-dialog {
  @apply absolute inset-x-3 bottom-3 top-3 mx-auto flex max-w-4xl flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-2xl;
}

@media (min-width: 768px) {
  .node-editor-dialog {
    @apply inset-x-6 bottom-auto top-[5vh] max-h-[90vh];
  }
}

.node-editor-header {
  @apply grid shrink-0 gap-3 border-b border-n-weak bg-n-solid-1 px-4 py-4 md:px-6;
}

.node-flow-breadcrumb {
  @apply flex min-w-0 flex-wrap items-center gap-1.5 rounded-lg bg-n-alpha-1 px-2 py-1.5 text-xs md:flex-nowrap;
}

.node-flow-step {
  @apply inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-n-slate-11 md:flex-1;
}

.node-flow-step:disabled {
  @apply cursor-default;
}

.node-flow-step.is-link {
  @apply cursor-pointer hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand;
}

.node-flow-menu {
  @apply absolute top-full z-10 mt-1 grid w-64 max-w-[80vw] gap-0.5 rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg;
}

.node-flow-menu-title {
  @apply mb-0 px-2 py-1 text-[11px] font-semibold uppercase text-n-slate-10;
}

.node-flow-menu-item {
  @apply flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-start text-xs text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand;
}

.node-flow-step.is-current {
  @apply bg-n-solid-1 text-n-slate-12 shadow-sm;
}

.node-flow-step-label {
  @apply shrink-0 font-medium uppercase text-n-slate-10;
}

.node-flow-step strong {
  @apply min-w-0 truncate font-semibold;
}

.node-editor-body {
  @apply grid min-h-0 flex-1 content-start gap-4 overflow-y-auto bg-n-background px-4 py-4 md:px-6 md:py-5;
}

.node-editor-section {
  @apply grid gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4;
}

.node-editor-section-heading {
  @apply flex items-start gap-3 text-n-slate-12;
}

.node-editor-section-heading > span:first-child {
  @apply mt-0.5 shrink-0 text-n-brand;
}

.node-editor-section-heading h3 {
  @apply mb-0 text-sm font-semibold text-n-slate-12;
}

.node-editor-section-heading p {
  @apply mb-0 mt-0.5 text-xs text-n-slate-10;
}

details.node-editor-section > summary {
  @apply list-none;
}

details.node-editor-section > summary::-webkit-details-marker {
  @apply hidden;
}

.node-editor-footer {
  @apply flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-n-weak bg-n-solid-1 px-4 py-3 md:px-6;
}
</style>
